import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Play, Plus, Trash2, AlertTriangle, Users, MapPin, Clock, Check, Download, Image as ImageIcon, FileDown, Save as SaveIcon, RotateCcw, History, Bookmark, ArrowLeftRight, Dumbbell, EyeOff, Eye as EyeIcon, Sparkles } from "lucide-react";
import { toPng } from "html-to-image";
import jsPDF from "jspdf";
import { DAYS, TIMES } from "@/lib/constants";
import { cn } from "@/lib/utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const COLORS = [
  "#DC2626", "#EA580C", "#D97706", "#CA8A04", "#65A30D", "#16A34A",
  "#059669", "#0D9488", "#0891B2", "#0284C7", "#2563EB", "#4F46E5",
  "#7C3AED", "#9333EA", "#C026D3", "#DB2777", "#E11D48", "#78350F",
  "#1F2937", "#475569", "#84CC16", "#22D3EE", "#F472B6", "#A855F7",
];

const TRAINER_COLORS = ["#F97316", "#DC2626", "#7C3AED", "#059669", "#0284C7", "#DB2777", "#CA8A04", "#475569"];

/**
 * Full calendar planner dialog: coach management, per-day settings, per-group settings, schedule generation, weekly view.
 * Props:
 *  - open, onOpenChange
 *  - token
 *  - groups, registrations, reloadGroups
 */
export default function CalendarPlanner({ open, onOpenChange, token, groups, registrations, reloadGroups, readOnly }) {
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [tab, setTab] = useState("groups");
  const [coaches, setCoaches] = useState([]);
  const [newCoach, setNewCoach] = useState("");
  const [courts, setCourts] = useState([]);
  const [config, setConfig] = useState({ day_settings: {} });
  const [schedule, setSchedule] = useState(null);
  const [editedSessions, setEditedSessions] = useState(null); // local edits, null = no unsaved changes
  const [undoStack, setUndoStack] = useState([]); // stack of prior editedSessions values for undo
  const [savingSched, setSavingSched] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [focusSession, setFocusSession] = useState(null);
  const [versions, setVersions] = useState([]);
  const [viewingVersion, setViewingVersion] = useState(null); // null = current latest
  const [saveVersionOpen, setSaveVersionOpen] = useState(false);
  const [newVersionName, setNewVersionName] = useState("");
  const [savingVersion, setSavingVersion] = useState(false);
  const [compareOpen, setCompareOpen] = useState(false);
  const [compareVersionId, setCompareVersionId] = useState("");
  const [viewMode, setViewMode] = useState("perDay"); // "perDay" | "perCourt"
  const [athleticTrainers, setAthleticTrainers] = useState([]);
  const [newTrainer, setNewTrainer] = useState("");
  const [athleticConfig, setAthleticConfig] = useState({ trainer_id: null, availability: { lun: ["16:30","17:00","17:30","18:00"], ven: ["16:30","17:00","17:30","18:00"] } });
  const [showAthletic, setShowAthletic] = useState(true);
  const calendarRef = React.useRef(null);

  const load = async () => {
    try {
      const [cRes, ctRes, cfgRes, schRes, vRes, atRes, acfgRes] = await Promise.all([
        axios.get(`${API}/admin/coaches`, auth),
        axios.get(`${API}/admin/courts`, auth),
        axios.get(`${API}/admin/calendar/config`, auth),
        axios.get(`${API}/admin/schedule/latest`, auth),
        axios.get(`${API}/admin/schedule/versions`, auth),
        axios.get(`${API}/admin/athletic/trainers`, auth),
        axios.get(`${API}/admin/athletic/config`, auth),
      ]);
      setCoaches(cRes.data);
      setCourts(ctRes.data);
      setConfig(cfgRes.data || { day_settings: {} });
      setSchedule(schRes.data);
      setVersions(vRes.data || []);
      setAthleticTrainers(atRes.data || []);
      if (acfgRes.data) setAthleticConfig(acfgRes.data);
    } catch {
      toast.error("Errore caricamento configurazione");
    }
  };

  const reloadVersions = async () => {
    try {
      const res = await axios.get(`${API}/admin/schedule/versions`, auth);
      setVersions(res.data || []);
    } catch { /* ignore */ }
  };

  const saveVersion = async () => {
    const nome = newVersionName.trim();
    if (!nome) { toast.error("Inserisci un nome"); return; }
    setSavingVersion(true);
    try {
      await axios.post(`${API}/admin/schedule/versions`, { nome }, auth);
      toast.success(`Versione "${nome}" salvata`);
      setSaveVersionOpen(false);
      setNewVersionName("");
      await reloadVersions();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore salvataggio versione");
    } finally {
      setSavingVersion(false);
    }
  };

  const restoreVersion = async (v) => {
    try {
      const res = await axios.post(`${API}/admin/schedule/versions/${v.id}/restore`, {}, auth);
      setSchedule(res.data);
      setEditedSessions(null);
      setViewingVersion(null);
      toast.success(`Versione "${v.nome}" ripristinata come corrente`);
    } catch {
      toast.error("Errore ripristino");
    }
  };

  const deleteVersion = async (v) => {
    try {
      await axios.delete(`${API}/admin/schedule/versions/${v.id}`, auth);
      toast.success("Versione eliminata");
      if (viewingVersion?.id === v.id) setViewingVersion(null);
      await reloadVersions();
    } catch {
      toast.error("Errore eliminazione");
    }
  };

  useEffect(() => { if (open) load(); /* eslint-disable-next-line */ }, [open]);

  const addCoach = async () => {
    const nome = newCoach.trim();
    if (!nome) return;
    try {
      const res = await axios.post(`${API}/admin/coaches`, { nome }, auth);
      setCoaches((prev) => [...prev, res.data].sort((a, b) => a.nome.localeCompare(b.nome)));
      setNewCoach("");
      toast.success("Maestro aggiunto");
    } catch {
      toast.error("Errore");
    }
  };

  const removeCoach = async (id) => {
    try {
      await axios.delete(`${API}/admin/coaches/${id}`, auth);
      setCoaches((prev) => prev.filter((c) => c.id !== id));
      toast.success("Maestro rimosso");
      reloadGroups && reloadGroups();
    } catch {
      toast.error("Errore");
    }
  };

  const addTrainer = async () => {
    const nome = newTrainer.trim();
    if (!nome) return;
    try {
      const res = await axios.post(`${API}/admin/athletic/trainers`, { nome }, auth);
      const next = [...athleticTrainers, res.data].sort((a, b) => a.nome.localeCompare(b.nome));
      setAthleticTrainers(next);
      setNewTrainer("");
      toast.success("Preparatore aggiunto");
    } catch {
      toast.error("Errore");
    }
  };

  const updateTrainer = async (id, patch) => {
    // optimistic update
    setAthleticTrainers((prev) => prev.map((t) => (t.id === id ? { ...t, ...patch } : t)));
    try {
      await axios.patch(`${API}/admin/athletic/trainers/${id}`, patch, auth);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore salvataggio preparatore");
      // best-effort re-fetch
      try {
        const res = await axios.get(`${API}/admin/athletic/trainers`, auth);
        setAthleticTrainers(res.data || []);
      } catch { /* ignore */ }
    }
  };

  const toggleTrainerEnabled = (t) => updateTrainer(t.id, { enabled: !t.enabled });

  const toggleTrainerSlot = (t, day, time) => {
    const current = t.availability?.[day] || [];
    const has = current.includes(time);
    const nextDay = has ? current.filter((x) => x !== time) : [...current, time].sort();
    const nextAvail = { ...(t.availability || {}), [day]: nextDay };
    updateTrainer(t.id, { availability: nextAvail });
  };

  const removeTrainer = async (id) => {
    try {
      await axios.delete(`${API}/admin/athletic/trainers/${id}`, auth);
      setAthleticTrainers((prev) => prev.filter((t) => t.id !== id));
      toast.success("Preparatore rimosso");
    } catch {
      toast.error("Errore");
    }
  };

  const updateGroupField = async (group, field, value) => {
    try {
      await axios.patch(`${API}/admin/groups/${group.id}`, { [field]: value }, auth);
      reloadGroups && reloadGroups();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore salvataggio");
    }
  };

  const toggleDayCourt = async (day, courtId) => {
    const ds = config.day_settings?.[day] || { court_ids: courts.map((c) => c.id), coach_ids: [] };
    const has = (ds.court_ids || []).includes(courtId);
    const next = {
      ...ds,
      court_ids: has ? ds.court_ids.filter((c) => c !== courtId) : [...(ds.court_ids || []), courtId],
    };
    const newDS = { ...(config.day_settings || {}), [day]: next };
    setConfig({ day_settings: newDS });
    try {
      await axios.put(`${API}/admin/calendar/config`, { day_settings: newDS }, auth);
    } catch { toast.error("Errore salvataggio giornata"); }
  };

  const toggleDayCoach = async (day, coachId) => {
    const ds = config.day_settings?.[day] || { court_ids: courts.map((c) => c.id), coach_ids: [] };
    const has = (ds.coach_ids || []).includes(coachId);
    const next = {
      ...ds,
      coach_ids: has ? ds.coach_ids.filter((c) => c !== coachId) : [...(ds.coach_ids || []), coachId],
    };
    const newDS = { ...(config.day_settings || {}), [day]: next };
    setConfig({ day_settings: newDS });
    try {
      await axios.put(`${API}/admin/calendar/config`, { day_settings: newDS }, auth);
    } catch { toast.error("Errore salvataggio giornata"); }
  };

  const toggleDayCourtHour = async (day, courtId, time) => {
    const ds = config.day_settings?.[day] || { court_ids: courts.map((c) => c.id), coach_ids: [], court_hours: {} };
    const currentCH = ds.court_hours || {};
    const list = currentCH[courtId] ?? [...TIMES];
    const has = list.includes(time);
    const nextList = has ? list.filter((t) => t !== time) : [...list, time].sort();
    const next = { ...ds, court_hours: { ...currentCH, [courtId]: nextList } };
    const newDS = { ...(config.day_settings || {}), [day]: next };
    setConfig({ day_settings: newDS });
    try {
      await axios.put(`${API}/admin/calendar/config`, { day_settings: newDS }, auth);
    } catch { toast.error("Errore salvataggio orari campo"); }
  };

  const setDayCourtHoursAll = async (day, courtId, allOn) => {
    const ds = config.day_settings?.[day] || { court_ids: courts.map((c) => c.id), coach_ids: [], court_hours: {} };
    const currentCH = ds.court_hours || {};
    const next = { ...ds, court_hours: { ...currentCH, [courtId]: allOn ? [...TIMES] : [] } };
    const newDS = { ...(config.day_settings || {}), [day]: next };
    setConfig({ day_settings: newDS });
    try {
      await axios.put(`${API}/admin/calendar/config`, { day_settings: newDS }, auth);
    } catch { toast.error("Errore salvataggio orari campo"); }
  };

  const generate = async (opts = {}) => {
    setGenerating(true);
    try {
      const res = await axios.post(
        `${API}/admin/schedule/generate`,
        { multi_run: !!opts.multiRun, annealing: true },
        auth,
      );
      setSchedule(res.data);
      setEditedSessions(null);
      setTab("calendar");
      toast.success(opts.multiRun ? "Calendario generato (ricerca approfondita)" : "Calendario generato");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore generazione");
    } finally {
      setGenerating(false);
    }
  };

  const [healthReport, setHealthReport] = useState(null);
  const [healthOpen, setHealthOpen] = useState(false);
  const [multiRun, setMultiRun] = useState(false);
  const runHealthCheck = async () => {
    try {
      const res = await axios.get(`${API}/admin/schedule/health-check`, auth);
      setHealthReport(res.data);
      setHealthOpen(true);
    } catch (err) {
      toast.error("Errore health-check");
    }
  };
  // Drag suggestions: while dragging a missing brick, highlight the 8 best (day, start_time)
  // combinations — deduped so at most ONE court per (day, time) is proposed (same time on
  // different courts is equivalent per user preferences). All consecutive cells covering the
  // session duration get the green ring (e.g. 90-min = 3 cells lit).
  const [suggestedSlots, setSuggestedSlots] = useState(new Set());
  const computeSuggestions = (groupId, duration) => {
    const g = groups.find((x) => x.id === groupId);
    if (!g) return new Set();
    const need = Math.max(1, Math.round((duration || g.session_duration_minutes || 60) / 30));
    const members = (g.registration_ids || [])
      .map((id) => registrations.find((r) => r.id === id))
      .filter(Boolean);
    const already = new Set();
    for (const s of (editedSessions ?? schedule?.sessions ?? [])) {
      if (s.type !== "athletic" && s.group_id === groupId) already.add(s.day);
    }
    const candidates = [];
    for (const d of DAYS) {
      const di = DAYS.indexOf(d);
      const close = [...already].some((ad) => Math.abs(DAYS.findIndex((x) => x.key === ad) - di) <= 1);
      if (close) continue;
      const courtHours = (config.day_settings?.[d.key]?.court_hours) || {};
      for (const c of courts) {
        for (let ti = 0; ti + need <= TIMES.length; ti++) {
          const times = TIMES.slice(ti, ti + need);
          const hoursForCourt = courtHours[c.id];
          if (hoursForCourt && !times.every((t) => hoursForCourt.includes(t))) continue;
          let score = 0;
          for (const m of members) {
            const disp = { ...(m.disponibilita || {}), ...(m.disponibilita_admin || {}) };
            let hasRed = false, allGreen = true, anyDeclared = false;
            for (const t of times) {
              const v = disp[`${d.key}_${t}`];
              if (v) anyDeclared = true;
              if (v === "red") hasRed = true;
              if (v !== "green") allGreen = false;
            }
            if (hasRed) score -= 3;
            else if (allGreen) score += 2;
            else if (anyDeclared) score += 1;
            else score -= 0.5;
          }
          candidates.push({ day: d.key, court: c.id, startTime: times[0], times: [...times], score });
        }
      }
    }
    // Sort by descending score; then diversify: keep at most ONE court per (day, start_time)
    candidates.sort((a, b) => b.score - a.score);
    const seen = new Set(); // "day_startTime"
    const picked = [];
    for (const cand of candidates) {
      const key = `${cand.day}_${cand.startTime}`;
      if (seen.has(key)) continue;
      seen.add(key);
      picked.push(cand);
      if (picked.length >= 8) break;
    }
    // Expand each pick to all covered cells (for multi-slot sessions like 90 or 120 min)
    const out = new Set();
    for (const p of picked) {
      for (const t of p.times) {
        out.add(`${p.day}_${p.court}_${t}`);
      }
    }
    return out;
  };

  const completeAuto = async () => {
    const base = editedSessions ?? schedule?.sessions ?? [];
    setGenerating(true);
    try {
      const res = await axios.post(
        `${API}/admin/schedule/complete`,
        { preplaced_sessions: base },
        auth,
      );
      setSchedule(res.data);
      setEditedSessions(null);
      toast.success("Calendario completato in automatico");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore completamento");
    } finally {
      setGenerating(false);
    }
  };

  const startEmpty = () => {
    setEditedSessions([]);
    setUndoStack([]);
    setSchedule({ generated_at: new Date().toISOString(), sessions: [], criticities: [] });
    toast.info("Calendario svuotato — trascina i blocchi dei gruppi negli slot");
    setTab("calendar");
  };

  const currentSessions = viewingVersion
    ? viewingVersion.sessions
    : (editedSessions ?? schedule?.sessions ?? []);
  const currentCriticities = viewingVersion
    ? viewingVersion.criticities
    : (schedule?.criticities ?? []);
  const hasEdits = !viewingVersion && editedSessions !== null;
  const isReadOnly = !!viewingVersion;

  // Sessions augmented with derived member-overlap conflicts: an athlete cannot be in two
  // sessions overlapping in time (tennis + tennis, or tennis + atletica). We recompute the
  // warnings each render and merge them into a per-session conflicts view.
  const displaySessions = useMemo(() => {
    const augmented = currentSessions.map((s) => ({ ...s, conflicts: [...(s.conflicts || [])] }));
    const membersByGroup = {};
    for (const g of groups) {
      membersByGroup[g.id] = g.registration_ids || [];
    }
    const regById = {};
    for (const r of registrations) regById[r.id] = r;
    for (let i = 0; i < augmented.length; i++) {
      const si = augmented[i];
      const mI = membersByGroup[si.group_id] || [];
      if (mI.length === 0) continue;
      const iStart = TIMES.indexOf(si.start_time);
      const iSlots = timeToSlots(si.start_time, si.end_time);
      for (let j = i + 1; j < augmented.length; j++) {
        const sj = augmented[j];
        if (sj.day !== si.day) continue;
        if (sj.group_id === si.group_id) continue; // same group already handled elsewhere
        const jStart = TIMES.indexOf(sj.start_time);
        const jSlots = timeToSlots(sj.start_time, sj.end_time);
        const overlap = iStart < jStart + jSlots && jStart < iStart + iSlots;
        if (!overlap) continue;
        const mJ = membersByGroup[sj.group_id] || [];
        const shared = mI.filter((id) => mJ.includes(id));
        if (shared.length === 0) continue;
        const names = shared.slice(0, 3).map((id) => {
          const r = regById[id];
          return r ? `${r.figlio.nome} ${r.figlio.cognome}` : id;
        });
        const suffix = shared.length > 3 ? `…+${shared.length - 3}` : "";
        const otherKind = sj.type === "athletic" ? "atletica" : "altro campo";
        const kindI = si.type === "athletic" ? "atletica" : "altro campo";
        augmented[i].conflicts.push(
          `Allievo${shared.length > 1 ? "i" : ""} in doppio con ${otherKind} (${sj.start_time}): ${names.join(", ")}${suffix}`
        );
        augmented[j].conflicts.push(
          `Allievo${shared.length > 1 ? "i" : ""} in doppio con ${kindI} (${si.start_time}): ${names.join(", ")}${suffix}`
        );
      }
    }
    return augmented;
  }, [currentSessions, groups, registrations]);

  const moveSession = (sessionIdx, newDay, newStartTime, newCourt) => {
    const sess = currentSessions[sessionIdx];
    if (!sess) return;
    const requestedSlots = timeToSlots(sess.start_time, sess.end_time);
    const requestedDuration = requestedSlots * 30;
    const startIdx = TIMES.indexOf(newStartTime);
    if (startIdx < 0) {
      toast.error("Slot non valido");
      return;
    }

    const conflictsList = [];
    // Preserve prior non-physical warnings? Reset — we recompute for the new position.

    // Physical overlap helper (excludes THIS session by identity)
    const hasPhysicalOverlap = (courts, slotsAttempt) => {
      const targetIndices = new Set();
      for (let i = 0; i < slotsAttempt; i++) targetIndices.add(startIdx + i);
      for (const other of currentSessions) {
        if (other === sess) continue;
        if (other.day !== newDay) continue;
        if (other.type === "athletic") continue;
        const otherOccupies = other.dual_court
          ? ["campo1", "campo2"]
          : [other.court_id];
        if (!courts.some((c) => otherOccupies.includes(c))) continue;
        const oStart = TIMES.indexOf(other.start_time);
        const oSlots = timeToSlots(other.start_time, other.end_time);
        for (let j = 0; j < oSlots; j++) {
          if (targetIndices.has(oStart + j)) return true;
        }
      }
      return false;
    };

    // Try dual-court placement when the session is dual and drop lands on campo1/2
    let useDualCourt = !!sess.dual_court && (newCourt === "campo1" || newCourt === "campo2");
    let placedCourt = useDualCourt ? "campo1" : newCourt;
    let occupyingCourts = useDualCourt ? ["campo1", "campo2"] : [placedCourt];
    let slots = requestedSlots;
    let duration = requestedDuration;

    if (hasPhysicalOverlap(occupyingCourts, slots)) {
      if (useDualCourt) {
        useDualCourt = false;
        placedCourt = newCourt;
        occupyingCourts = [newCourt];
        conflictsList.push("Doppio campo non disponibile: sessione su singolo campo");
        if (hasPhysicalOverlap(occupyingCourts, slots)) {
          if (slots > 2 && !hasPhysicalOverlap(occupyingCourts, 2)) {
            conflictsList.push(`Durata ridotta da ${duration} a 60 min per conflitto slot`);
            slots = 2; duration = 60;
          } else {
            toast.error("Slot occupato: nessuno spazio libero qui");
            return;
          }
        }
      } else {
        if (slots > 2 && !hasPhysicalOverlap(occupyingCourts, 2)) {
          conflictsList.push(`Durata ridotta da ${duration} a 60 min per conflitto slot`);
          slots = 2; duration = 60;
        } else {
          toast.error("Slot occupato: nessuno spazio libero qui");
          return;
        }
      }
    }

    if (sess.dual_court && !useDualCourt && newCourt !== "campo1" && newCourt !== "campo2") {
      conflictsList.push("Doppio campo disatteso: piazzato su singolo campo");
    }

    if (startIdx + slots > TIMES.length) {
      conflictsList.push("Sessione sfora oltre l'orario di chiusura del calendario");
    }

    // Court closed hours (soft)
    const ds = config.day_settings?.[newDay];
    const courtHours = ds?.court_hours?.[placedCourt];
    if (courtHours && courtHours.length > 0) {
      const closedTimes = [];
      for (let i = 0; i < slots; i++) {
        const t = TIMES[startIdx + i];
        if (t && !courtHours.includes(t)) closedTimes.push(t);
      }
      if (closedTimes.length > 0) {
        conflictsList.push(`Campo chiuso alle ${closedTimes.join(", ")}`);
      }
    }
    if (useDualCourt) {
      const ch2 = ds?.court_hours?.campo2;
      if (ch2 && ch2.length > 0) {
        const closed2 = [];
        for (let i = 0; i < slots; i++) {
          const t = TIMES[startIdx + i];
          if (t && !ch2.includes(t)) closed2.push(t);
        }
        if (closed2.length > 0) conflictsList.push(`Campo 2 chiuso alle ${closed2.join(", ")}`);
      }
    }

    // Coach conflict (soft)
    const coachIds = sess.coach_ids || [];
    if (coachIds.length > 0) {
      const targetIndices = new Set();
      for (let i = 0; i < slots; i++) targetIndices.add(startIdx + i);
      let coachBusy = false;
      for (const other of currentSessions) {
        if (other === sess) continue;
        if (other.day !== newDay) continue;
        if (other.type === "athletic") continue;
        if (!other.coach_ids?.some((c) => coachIds.includes(c))) continue;
        const oStart = TIMES.indexOf(other.start_time);
        const oSlots = timeToSlots(other.start_time, other.end_time);
        for (let j = 0; j < oSlots; j++) {
          if (targetIndices.has(oStart + j)) { coachBusy = true; break; }
        }
        if (coachBusy) break;
      }
      if (coachBusy) conflictsList.push("Maestro già impegnato in altra sessione");
    }

    // Members "red" / missing declarations (soft warnings)
    const g = groups.find((x) => x.id === sess.group_id);
    if (g) {
      const memberIds = g.registration_ids || [];
      const membersWithRed = [];
      for (const mid of memberIds) {
        const reg = registrations.find((r) => r.id === mid);
        if (!reg) continue;
        const disp = { ...(reg.disponibilita || {}), ...(reg.disponibilita_admin || {}) };
        for (let i = 0; i < slots; i++) {
          const t = TIMES[startIdx + i];
          if (t && disp[`${newDay}_${t}`] === "red") {
            membersWithRed.push(`${reg.figlio.nome} ${reg.figlio.cognome}`);
            break;
          }
        }
      }
      if (membersWithRed.length > 0) {
        conflictsList.push(`"Impossibile" per: ${membersWithRed.slice(0, 3).join(", ")}${membersWithRed.length > 3 ? "…" : ""}`);
      }
      const membersMissing = [];
      for (const mid of memberIds) {
        const reg = registrations.find((r) => r.id === mid);
        if (!reg) continue;
        const disp = { ...(reg.disponibilita || {}), ...(reg.disponibilita_admin || {}) };
        let anyDeclared = false;
        for (let i = 0; i < slots; i++) {
          const t = TIMES[startIdx + i];
          if (t && disp[`${newDay}_${t}`]) { anyDeclared = true; break; }
        }
        if (!anyDeclared) membersMissing.push(`${reg.figlio.nome}`);
      }
      if (membersMissing.length > 0) {
        conflictsList.push(`${membersMissing.length} allievi senza preferenza dichiarata`);
      }
    }

    const endTime = indexToEndTime(startIdx, slots);
    const nextSessions = [...currentSessions];
    nextSessions[sessionIdx] = {
      ...sess,
      day: newDay,
      start_time: newStartTime,
      end_time: endTime,
      court_id: placedCourt,
      dual_court: useDualCourt,
      conflicts: conflictsList,
    };
    setUndoStack((s) => [...s.slice(-19), editedSessions]);
    setEditedSessions(nextSessions);
    if (conflictsList.length > 0) {
      toast.warning(`Spostato con ${conflictsList.length} avviso${conflictsList.length === 1 ? "" : "i"} — vedi ⚠ sul blocco`);
    }
  };

  // Move an entire athletic bucket (all sessions sharing day+start_time) to a new day+start_time.
  // If destination already has an athletic bucket with the SAME `athletic_shared_label`, MERGE:
  // the moved bucket's sessions inherit the destination start/end (which are identical anyway
  // for 60-min athletic sessions) and the two buckets coexist at the same slot.
  const moveAthleticBucket = (oldDay, oldStartTime, newDay, newStartTime) => {
    const startIdx = TIMES.indexOf(newStartTime);
    if (startIdx < 0) {
      toast.error("Slot atletica non valido");
      return;
    }
    const bucket = [];
    currentSessions.forEach((s, i) => {
      if (s.type === "athletic" && s.day === oldDay && s.start_time === oldStartTime) {
        bucket.push({ session: s, idx: i });
      }
    });
    if (bucket.length === 0) return;
    if (oldDay === newDay && oldStartTime === newStartTime) return;

    const slots = timeToSlots(bucket[0].session.start_time, bucket[0].session.end_time);
    const sourceLabel = ((bucket[0].session.athletic_label) || "").trim().toLowerCase();

    if (startIdx + slots > TIMES.length) {
      toast.error("Slot atletica sfora oltre l'orario di chiusura");
      return;
    }

    // Look at any athletic session already occupying destination slots (excluding self)
    let mergeMode = false;
    let clashingLabel = null;
    for (const s of currentSessions) {
      if (s.type !== "athletic") continue;
      if (bucket.some((b) => b.session === s)) continue;
      if (s.day !== newDay) continue;
      const oStart = TIMES.indexOf(s.start_time);
      const oSlots = timeToSlots(s.start_time, s.end_time);
      let overlaps = false;
      for (let j = 0; j < oSlots && !overlaps; j++) {
        for (let i = 0; i < slots; i++) {
          if (oStart + j === startIdx + i) { overlaps = true; break; }
        }
      }
      if (!overlaps) continue;
      const otherLabel = ((s.athletic_label) || "").trim().toLowerCase();
      if (otherLabel && otherLabel === sourceLabel) {
        mergeMode = true; // same label → will merge into destination bucket
      } else {
        clashingLabel = s.athletic_label || "(senza etichetta)";
        break;
      }
    }
    if (clashingLabel) {
      toast.error(`Slot atletica occupato da un'altra sessione (etichetta "${clashingLabel}")`);
      return;
    }

    // Trainer availability soft check
    const conflicts = [];
    const trainerId = bucket[0].session.coach_ids?.[0];
    const tr = (athleticTrainers || []).find((x) => x.id === trainerId);
    if (tr && tr.availability) {
      const missing = [];
      for (let i = 0; i < slots; i++) {
        const t = TIMES[startIdx + i];
        if (!tr.availability?.[newDay]?.includes(t)) missing.push(t);
      }
      if (missing.length > 0) {
        conflicts.push(`Preparatore atletico non disponibile alle ${missing.join(", ")}`);
      }
    }

    const newEnd = indexToEndTime(startIdx, slots);
    const next = [...currentSessions];
    for (const { idx, session } of bucket) {
      next[idx] = {
        ...session,
        day: newDay,
        start_time: newStartTime,
        end_time: newEnd,
        conflicts: [...conflicts],
      };
    }
    setUndoStack((s) => [...s.slice(-19), editedSessions]);
    setEditedSessions(next);
    if (mergeMode) {
      toast.success(`Atletica unita nello stesso slot (stessa etichetta)`);
    } else if (conflicts.length > 0) {
      toast.warning(`Atletica spostata con ${conflicts.length} avviso${conflicts.length === 1 ? "" : "i"}`);
    } else {
      toast.success("Atletica spostata");
    }
  };

  // Create an athletic session for a group from the missing pool, dropped at (day, time).
  // Same-label bucket at the destination → merge; different-label overlap → hard block.
  const createAthleticFromMissing = (groupId, newDay, newStartTime) => {
    const g = groups.find((x) => x.id === groupId);
    if (!g || !g.athletic_enabled) {
      toast.error("Gruppo senza preparazione atletica");
      return;
    }
    const startIdx = TIMES.indexOf(newStartTime);
    if (startIdx < 0) { toast.error("Slot atletica non valido"); return; }
    const slots = 2; // 60 min
    if (startIdx + slots > TIMES.length) {
      toast.error("Sessione atletica sfora oltre l'orario di chiusura");
      return;
    }
    const label = (g.athletic_shared_label || "").trim();
    const labelKey = label.toLowerCase();

    // Overlap check on destination slots
    let clashingLabel = null;
    for (const s of currentSessions) {
      if (s.type !== "athletic") continue;
      if (s.day !== newDay) continue;
      const oStart = TIMES.indexOf(s.start_time);
      const oSlots = timeToSlots(s.start_time, s.end_time);
      let overlaps = false;
      for (let j = 0; j < oSlots && !overlaps; j++) {
        for (let i = 0; i < slots; i++) {
          if (oStart + j === startIdx + i) { overlaps = true; break; }
        }
      }
      if (!overlaps) continue;
      const otherLabel = ((s.athletic_label) || "").trim().toLowerCase();
      if (labelKey && otherLabel === labelKey) {
        // Merge OK. Continue scanning for potential clashes with different labels.
      } else {
        clashingLabel = s.athletic_label || "(senza etichetta)";
        break;
      }
    }
    if (clashingLabel) {
      toast.error(`Slot atletica occupato da un'altra sessione (etichetta "${clashingLabel}")`);
      return;
    }

    const trainerId = g.preferred_trainer_id || athleticTrainers.find((t) => t.enabled !== false)?.id;
    if (!trainerId) {
      toast.error("Nessun preparatore atletico abilitato");
      return;
    }
    const tr = athleticTrainers.find((t) => t.id === trainerId);
    const conflicts = [];
    if (tr && tr.availability) {
      const missing = [];
      for (let i = 0; i < slots; i++) {
        const t = TIMES[startIdx + i];
        if (!tr.availability?.[newDay]?.includes(t)) missing.push(t);
      }
      if (missing.length > 0) {
        conflicts.push(`Preparatore atletico non disponibile alle ${missing.join(", ")}`);
      }
    }
    const endTime = indexToEndTime(startIdx, slots);
    const newSession = {
      group_id: groupId,
      day: newDay,
      start_time: newStartTime,
      end_time: endTime,
      court_id: "atletica",
      coach_ids: [trainerId],
      score: 0,
      conflicts,
      type: "athletic",
      athletic_label: label,
      dual_court: false,
    };
    setUndoStack((s) => [...s.slice(-19), editedSessions]);
    setEditedSessions([...currentSessions, newSession]);
    if (conflicts.length > 0) {
      toast.warning(`Atletica piazzata con ${conflicts.length} avviso — vedi ⚠ sul blocco`);
    } else {
      toast.success(label ? `Atletica piazzata (etichetta "${label}")` : "Atletica piazzata");
    }
  };

  const saveEdits = async () => {
    if (!editedSessions) return;
    setSavingSched(true);
    try {
      const res = await axios.put(
        `${API}/admin/schedule/latest`,
        { sessions: editedSessions, criticities: schedule?.criticities || [] },
        auth,
      );
      setSchedule(res.data);
      setEditedSessions(null);
      setUndoStack([]);
      toast.success("Modifiche salvate");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore salvataggio");
    } finally {
      setSavingSched(false);
    }
  };

  const discardEdits = () => {
    setEditedSessions(null);
    setUndoStack([]);
    toast.info("Modifiche annullate");
  };

  // Toggle whether a group member is expected in this weekly slot (affects attendance stats)
  const toggleExpected = (session, regId) => {
    const same = (s) => s.group_id === session.group_id && s.day === session.day
      && s.start_time === session.start_time && s.court_id === session.court_id && s.type === session.type;
    const idx = currentSessions.findIndex(same);
    if (idx < 0) return;
    const cur = currentSessions[idx].excluded_ids || [];
    const excluded_ids = cur.includes(regId) ? cur.filter((x) => x !== regId) : [...cur, regId];
    const next = currentSessions.map((s, i) => (i === idx ? { ...s, excluded_ids } : s));
    setUndoStack((s) => [...s.slice(-19), editedSessions]);
    setEditedSessions(next);
    setFocusSession((f) => (f ? { ...f, excluded_ids } : f));
  };

  const undoLast = () => {
    if (undoStack.length === 0) return;
    const prev = undoStack[undoStack.length - 1];
    setUndoStack((s) => s.slice(0, -1));
    setEditedSessions(prev);
    toast.info("Ultima azione annullata");
  };

  const deleteSession = (sessionIdx) => {
    const sess = currentSessions[sessionIdx];
    if (!sess) return;
    const nextSessions = currentSessions.filter((_, i) => i !== sessionIdx);
    setUndoStack((s) => [...s.slice(-19), editedSessions]);
    setEditedSessions(nextSessions);
    const grp = groups.find((g) => g.id === sess.group_id);
    toast.info(`Sessione di "${grp?.nome || '?'}" rimossa — torna nel riquadro sessioni mancanti`);
  };

  // Delete an entire athletic bucket (all sessions sharing day+start_time)
  const deleteAthleticBucket = (day, time) => {
    const toRemove = currentSessions.filter(
      (s) => s.type === "athletic" && s.day === day && s.start_time === time
    );
    if (toRemove.length === 0) return;
    const next = currentSessions.filter((s) => !toRemove.includes(s));
    setUndoStack((s) => [...s.slice(-19), editedSessions]);
    setEditedSessions(next);
    toast.info(`Blocco atletica rimosso (${toRemove.length} sessione${toRemove.length === 1 ? "" : " unite"})`);
  };

  const createSessionFromMissing = (groupId, newDay, newStartTime, newCourt, durationOverride) => {
    const g = groups.find((x) => x.id === groupId);
    if (!g) return;
    const requestedDuration = durationOverride || g.session_duration_minutes || 60;
    const requestedSlots = Math.max(1, Math.round(requestedDuration / 30));
    const startIdx = TIMES.indexOf(newStartTime);
    if (startIdx < 0) {
      toast.error("Slot non valido");
      return;
    }

    const conflictsList = [];

    // Overlap helper: returns true if a physical overlap exists on any of `courts`
    // for the given day starting at startIdx spanning `slots` half-hours.
    const hasPhysicalOverlap = (courts, slotsAttempt) => {
      const targetIndices = new Set();
      for (let i = 0; i < slotsAttempt; i++) targetIndices.add(startIdx + i);
      for (const other of currentSessions) {
        if (other.day !== newDay) continue;
        if (other.type === "athletic") continue;
        const otherOccupies = other.dual_court
          ? ["campo1", "campo2"]
          : [other.court_id];
        if (!courts.some((c) => otherOccupies.includes(c))) continue;
        const oStart = TIMES.indexOf(other.start_time);
        const oSlots = timeToSlots(other.start_time, other.end_time);
        for (let j = 0; j < oSlots; j++) {
          if (targetIndices.has(oStart + j)) return true;
        }
      }
      return false;
    };

    // Determine placement court(s). Try dual first if applicable.
    let useDualCourt = !!g.dual_court && (newCourt === "campo1" || newCourt === "campo2");
    let placedCourt = useDualCourt ? "campo1" : newCourt;
    let occupyingCourts = useDualCourt ? ["campo1", "campo2"] : [placedCourt];
    let slots = requestedSlots;
    let duration = requestedDuration;

    // 1) Try requested duration on dual (if applicable) or dropped court
    if (hasPhysicalOverlap(occupyingCourts, slots)) {
      // 2) If dual, fall back to single court on the dropped court
      if (useDualCourt) {
        useDualCourt = false;
        placedCourt = newCourt;
        occupyingCourts = [newCourt];
        conflictsList.push("Doppio campo non disponibile: sessione su singolo campo");
        // Retry with same slots on single court
        if (hasPhysicalOverlap(occupyingCourts, slots)) {
          // 3) Try duration reduction to 60 min (2 slots) minimum
          if (slots > 2 && !hasPhysicalOverlap(occupyingCourts, 2)) {
            conflictsList.push(`Durata ridotta da ${duration} a 60 min per conflitto slot`);
            slots = 2;
            duration = 60;
          } else {
            toast.error("Slot occupato: nessuno spazio libero qui");
            return;
          }
        }
      } else {
        // Single court: try duration reduction to 60 min
        if (slots > 2 && !hasPhysicalOverlap(occupyingCourts, 2)) {
          conflictsList.push(`Durata ridotta da ${duration} a 60 min per conflitto slot`);
          slots = 2;
          duration = 60;
        } else {
          toast.error("Slot occupato: nessuno spazio libero qui");
          return;
        }
      }
    }

    // Doppio-campo disatteso quando l'utente rilascia su un campo non "1/2"
    if (g.dual_court && !useDualCourt && newCourt !== "campo1" && newCourt !== "campo2") {
      conflictsList.push("Doppio campo disatteso: piazzato su singolo campo");
    }

    // Out of grid (sfora chiusura calendario)
    if (startIdx + slots > TIMES.length) {
      conflictsList.push("Sessione sfora oltre l'orario di chiusura del calendario");
    }

    // Court closed hours (soft warning)
    const ds = config.day_settings?.[newDay];
    const courtHours = ds?.court_hours?.[placedCourt];
    if (courtHours && courtHours.length > 0) {
      const closedTimes = [];
      for (let i = 0; i < slots; i++) {
        const t = TIMES[startIdx + i];
        if (t && !courtHours.includes(t)) closedTimes.push(t);
      }
      if (closedTimes.length > 0) {
        conflictsList.push(`Campo chiuso alle ${closedTimes.join(", ")}`);
      }
    }

    // Coach conflict (soft warning)
    if (g.coach_ids?.length > 0) {
      const targetIndices = new Set();
      for (let i = 0; i < slots; i++) targetIndices.add(startIdx + i);
      let coachBusy = false;
      for (const other of currentSessions) {
        if (other.day !== newDay) continue;
        if (other.type === "athletic") continue;
        if (!other.coach_ids?.some((c) => g.coach_ids.includes(c))) continue;
        const oStart = TIMES.indexOf(other.start_time);
        const oSlots = timeToSlots(other.start_time, other.end_time);
        for (let j = 0; j < oSlots; j++) {
          if (targetIndices.has(oStart + j)) { coachBusy = true; break; }
        }
        if (coachBusy) break;
      }
      if (coachBusy) {
        conflictsList.push("Maestro già impegnato in altra sessione");
      }
    }

    // Members with "red" on any slot → warning
    const memberIds = g.registration_ids || [];
    const membersWithRed = [];
    for (const mid of memberIds) {
      const reg = registrations.find((r) => r.id === mid);
      if (!reg) continue;
      const disp = { ...(reg.disponibilita || {}), ...(reg.disponibilita_admin || {}) };
      for (let i = 0; i < slots; i++) {
        const t = TIMES[startIdx + i];
        if (t && disp[`${newDay}_${t}`] === "red") {
          membersWithRed.push(`${reg.figlio.nome} ${reg.figlio.cognome}`);
          break;
        }
      }
    }
    if (membersWithRed.length > 0) {
      conflictsList.push(`"Impossibile" per: ${membersWithRed.slice(0, 3).join(", ")}${membersWithRed.length > 3 ? "…" : ""}`);
    }

    // Missing declarations warning
    const membersMissing = [];
    for (const mid of memberIds) {
      const reg = registrations.find((r) => r.id === mid);
      if (!reg) continue;
      const disp = { ...(reg.disponibilita || {}), ...(reg.disponibilita_admin || {}) };
      let anyDeclared = false;
      for (let i = 0; i < slots; i++) {
        const t = TIMES[startIdx + i];
        if (t && disp[`${newDay}_${t}`]) { anyDeclared = true; break; }
      }
      if (!anyDeclared) membersMissing.push(`${reg.figlio.nome}`);
    }
    if (membersMissing.length > 0) {
      conflictsList.push(`${membersMissing.length} allievi senza preferenza dichiarata`);
    }

    const endTime = indexToEndTime(startIdx, slots);
    const newSession = {
      group_id: groupId,
      day: newDay,
      start_time: newStartTime,
      end_time: endTime,
      court_id: placedCourt,
      coach_ids: g.coach_ids || [],
      score: 0,
      conflicts: conflictsList,
      type: "tennis",
      athletic_label: "",
      dual_court: useDualCourt,
    };
    setUndoStack((s) => [...s.slice(-19), editedSessions]);
    setEditedSessions([...currentSessions, newSession]);

    if (conflictsList.length > 0) {
      toast.warning(`Piazzato con ${conflictsList.length} avviso${conflictsList.length === 1 ? "" : "i"} — vedi ⚠ sul blocco`);
    } else {
      toast.success("Sessione piazzata");
    }
  };

  // Missing sessions pool: one brick per (group, sessionIndex) — each with its own duration.
  // Also includes athletic bricks for athletic-enabled groups (1 per group per week).
  const missingSessions = useMemo(() => {
    // Compute per-group already-placed durations (tennis) and athletic count (athletic)
    const placedByGroup = {};
    const athleticPlacedByGroup = {};
    for (const s of currentSessions) {
      if (s.type === "athletic") {
        athleticPlacedByGroup[s.group_id] = (athleticPlacedByGroup[s.group_id] || 0) + 1;
        continue;
      }
      const dur = timeToSlots(s.start_time, s.end_time) * 30;
      (placedByGroup[s.group_id] = placedByGroup[s.group_id] || []).push(dur);
    }
    const out = [];
    for (const g of groups) {
      const need = g.sessions_per_week ?? 1;
      if (need > 0) {
        let durs = (g.session_durations && g.session_durations.length)
          ? [...g.session_durations]
          : Array(need).fill(g.session_duration_minutes || 60);
        while (durs.length < need) durs.push(g.session_duration_minutes || 60);
        durs = durs.slice(0, need);
        const remaining = [...durs];
        for (const pd of (placedByGroup[g.id] || [])) {
          const idx = remaining.indexOf(pd);
          if (idx >= 0) remaining.splice(idx, 1);
          else if (remaining.length) remaining.pop();
        }
        remaining.forEach((d, i) => {
          out.push({ kind: "tennis", groupId: g.id, group: g, index: i, duration: d });
        });
      }
      // Athletic: 1 per athletic-enabled group; skip if already placed
      if (g.athletic_enabled && (athleticPlacedByGroup[g.id] || 0) === 0) {
        out.push({
          kind: "athletic",
          groupId: g.id,
          group: g,
          index: 0,
          duration: 60,
          label: (g.athletic_shared_label || "").trim(),
        });
      }
    }
    return out;
  }, [currentSessions, groups]);

  const exportPng = async () => {
    if (!calendarRef.current) return;
    setExporting(true);
    try {
      const dataUrl = await toPng(calendarRef.current, {
        backgroundColor: "#ffffff",
        pixelRatio: 2,
      });
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = `calendario_ctmontecchio_${Date.now()}.png`;
      a.click();
      toast.success("PNG scaricato");
    } catch {
      toast.error("Errore export PNG");
    } finally {
      setExporting(false);
    }
  };

  const exportPdf = async () => {
    if (!calendarRef.current) return;
    setExporting(true);
    try {
      const dataUrl = await toPng(calendarRef.current, {
        backgroundColor: "#ffffff",
        pixelRatio: 2,
      });
      const img = new Image();
      img.src = dataUrl;
      await new Promise((res) => { img.onload = res; });
      const pdf = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const margin = 24;
      const availW = pageW - margin * 2;
      const availH = pageH - margin * 2 - 40;
      const ratio = Math.min(availW / img.width, availH / img.height);
      const w = img.width * ratio;
      const h = img.height * ratio;
      pdf.setFontSize(14);
      pdf.text("Circolo Tennis Montecchio — Calendario Corsi", margin, margin + 4);
      pdf.setFontSize(9);
      pdf.setTextColor(100);
      pdf.text(new Date().toLocaleString("it-IT"), margin, margin + 20);
      pdf.setTextColor(0);
      pdf.addImage(dataUrl, "PNG", margin + (availW - w) / 2, margin + 34, w, h);
      pdf.save(`calendario_ctmontecchio_${Date.now()}.pdf`);
      toast.success("PDF scaricato");
    } catch {
      toast.error("Errore export PDF");
    } finally {
      setExporting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[92vh] overflow-y-auto" data-testid="calendar-planner-dialog">
        <DialogHeader>
          <DialogTitle className="text-2xl">Genera Calendario Corsi</DialogTitle>
          <DialogDescription>
            Configura maestri, giornate e gruppi, poi genera automaticamente la settimana di allenamenti.
          </DialogDescription>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab} className="mt-3">
          <TabsList className="grid grid-cols-5 w-full">
            <TabsTrigger value="coaches" data-testid="tab-coaches">Maestri</TabsTrigger>
            <TabsTrigger value="athletic" data-testid="tab-athletic">Atletica</TabsTrigger>
            <TabsTrigger value="days" data-testid="tab-days">Giornate</TabsTrigger>
            <TabsTrigger value="groups" data-testid="tab-groups">Gruppi</TabsTrigger>
            <TabsTrigger value="calendar" data-testid="tab-calendar">Calendario</TabsTrigger>
          </TabsList>

          {/* Coaches */}
          <TabsContent value="coaches" className="mt-4 space-y-3">
            <div className="flex gap-2">
              <Input
                placeholder="Nome maestro"
                value={newCoach}
                onChange={(e) => setNewCoach(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addCoach())}
                data-testid="new-coach-input"
              />
              <Button onClick={addCoach} className="bg-[#15803D] hover:bg-[#166534] gap-1.5" data-testid="add-coach-btn">
                <Plus className="w-4 h-4" />
                Aggiungi
              </Button>
            </div>
            {coaches.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-6">Nessun maestro inserito.</p>
            ) : (
              <div className="space-y-1">
                {coaches.map((c) => (
                  <div key={c.id} className="flex items-center justify-between px-3 py-2 rounded-lg border border-slate-200 bg-white" data-testid={`coach-row-${c.id}`}>
                    <span className="text-sm font-semibold text-slate-800">{c.nome}</span>
                    <Button variant="ghost" size="sm" onClick={() => removeCoach(c.id)} className="text-rose-600 hover:bg-rose-50" data-testid={`delete-coach-${c.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Athletic Trainer */}
          <TabsContent value="athletic" className="mt-4 space-y-3">
            <div className="rounded-xl bg-orange-50/70 border border-orange-200 p-3 text-xs text-orange-900 flex gap-2 items-start">
              <Dumbbell className="w-4 h-4 mt-0.5 shrink-0" />
              <div>
                <div className="font-bold mb-0.5">Preparazione atletica</div>
                Configura i preparatori atletici e gli orari in cui sono disponibili. La preparazione atletica dura 60 min ed è sempre in sequenza al tennis (prima o dopo). Nei gruppi puoi attivare l'atletica e assegnare un'etichetta condivisa per far fare atletica insieme a più gruppi nella stessa ora.
              </div>
            </div>

            <div className="flex gap-2">
              <Input
                placeholder="Nome preparatore atletico"
                value={newTrainer}
                onChange={(e) => setNewTrainer(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addTrainer())}
                data-testid="new-trainer-input"
              />
              <Button onClick={addTrainer} className="bg-orange-600 hover:bg-orange-700 gap-1.5" data-testid="add-trainer-btn">
                <Plus className="w-4 h-4" /> Aggiungi
              </Button>
            </div>

            {athleticTrainers.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-4">Nessun preparatore inserito.</p>
            ) : (
              <div className="space-y-3">
                {athleticTrainers.map((t) => {
                  const enabled = t.enabled !== false;
                  return (
                    <Card key={t.id} className={cn(
                      "card-warm transition-opacity",
                      enabled ? "" : "opacity-60"
                    )}>
                      <CardContent className="p-4 space-y-3">
                        <div className="flex items-center justify-between gap-2" data-testid={`trainer-row-${t.id}`}>
                          <div className="flex items-center gap-2">
                            <Dumbbell className={cn("w-4 h-4", enabled ? "text-orange-600" : "text-slate-400")} />
                            <span className="text-sm font-bold text-slate-900">{t.nome}</span>
                            {enabled ? (
                              <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-orange-500 text-white">
                                Abilitato
                              </span>
                            ) : (
                              <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-slate-200 text-slate-600">
                                Disabilitato
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-1">
                            <button
                              type="button"
                              role="switch"
                              aria-checked={enabled}
                              onClick={() => toggleTrainerEnabled(t)}
                              data-testid={`trainer-toggle-${t.id}`}
                              title={enabled ? "Disabilita preparatore" : "Abilita preparatore"}
                              className={cn(
                                "relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors",
                                enabled ? "bg-orange-500" : "bg-slate-300"
                              )}
                            >
                              <span className={cn(
                                "inline-block h-4 w-4 rounded-full bg-white shadow transform transition-transform",
                                enabled ? "translate-x-4" : "translate-x-0.5"
                              )} />
                            </button>
                            <Button variant="ghost" size="sm" onClick={() => removeTrainer(t.id)} className="text-rose-600 hover:bg-rose-50" data-testid={`delete-trainer-${t.id}`}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>

                        <div className={cn(!enabled && "pointer-events-none")}>
                          <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1.5 flex items-center gap-1">
                            <span className="w-3 h-3 rounded-full inline-block" style={{ background: t.color || "#F97316" }} />
                            Colore preparatore
                          </div>
                          <div className="flex flex-wrap gap-1.5 mb-2">
                            {TRAINER_COLORS.map((col) => {
                              const usedBy = athleticTrainers.find(
                                (o) => o.id !== t.id && (o.color || "").toLowerCase() === col.toLowerCase()
                              );
                              const isCurrent = (t.color || "").toLowerCase() === col.toLowerCase();
                              const disabled = !!usedBy && !isCurrent;
                              return (
                                <button
                                  key={col}
                                  type="button"
                                  onClick={() => !disabled && updateTrainer(t.id, { color: col })}
                                  disabled={disabled}
                                  data-testid={`trainer-${t.id}-color-${col}`}
                                  title={disabled ? `Già usato da "${usedBy.nome}"` : col}
                                  className={cn(
                                    "relative w-6 h-6 rounded-full border-2 transition-transform",
                                    isCurrent
                                      ? "border-slate-900 scale-110 ring-2 ring-slate-300"
                                      : disabled
                                        ? "border-white opacity-30 cursor-not-allowed"
                                        : "border-white shadow hover:scale-110"
                                  )}
                                  style={{ background: col }}
                                >
                                  {disabled && (
                                    <span className="absolute inset-0 flex items-center justify-center text-white text-[10px] font-bold">×</span>
                                  )}
                                </button>
                              );
                            })}
                          </div>
                          <div className="flex items-center gap-2 mb-3">
                            <label
                              className="relative w-9 h-8 rounded-md border border-slate-300 overflow-hidden cursor-pointer shadow-sm hover:shadow"
                              title="Apri palette completa"
                              style={{ background: t.color || "#F97316" }}
                            >
                              <input
                                type="color"
                                value={t.color || "#F97316"}
                                onChange={(e) => updateTrainer(t.id, { color: e.target.value.toUpperCase() })}
                                data-testid={`trainer-${t.id}-color-picker`}
                                className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
                              />
                            </label>
                            <Input
                              type="text"
                              value={t.color || ""}
                              placeholder="#RRGGBB"
                              onChange={(e) => {
                                const v = e.target.value.trim().toUpperCase();
                                if (/^#[0-9A-F]{0,6}$/.test(v)) updateTrainer(t.id, { color: v });
                              }}
                              data-testid={`trainer-${t.id}-color-hex`}
                              className="h-8 w-28 font-mono text-xs uppercase"
                              maxLength={7}
                            />
                          </div>
                          <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1.5 flex items-center gap-1">
                            <Clock className="w-3 h-3 text-orange-600" /> Disponibilità (slot 30 min)
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            {DAYS.map((d) => {
                              const dayAvail = (t.availability?.[d.key]) || [];
                              return (
                                <div key={d.key} className="rounded-lg border border-slate-200 p-2 bg-white">
                                  <div className="text-xs font-bold text-slate-900 mb-1.5">{d.label}</div>
                                  <div className="grid grid-cols-5 gap-1">
                                    {TIMES.map((tt) => {
                                      const active = dayAvail.includes(tt);
                                      return (
                                        <button
                                          key={tt}
                                          type="button"
                                          onClick={() => toggleTrainerSlot(t, d.key, tt)}
                                          data-testid={`trainer-${t.id}-slot-${d.key}-${tt}`}
                                          className={cn(
                                            "text-[10px] font-mono py-1 rounded border transition-colors",
                                            active
                                              ? "bg-orange-500 text-white border-orange-500"
                                              : "bg-slate-50 text-slate-500 border-slate-200 hover:bg-slate-100"
                                          )}
                                        >
                                          {tt}
                                        </button>
                                      );
                                    })}
                                  </div>
                                  {dayAvail.length > 0 && (
                                    <div className="mt-1.5 text-[10px] text-slate-500">
                                      Copertura: {dayAvail[0]}–{addMin(dayAvail[dayAvail.length - 1], 30)}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* Days */}
          <TabsContent value="days" className="mt-4 space-y-3">
            <p className="text-xs text-slate-500">
              Per ogni giornata, seleziona i campi e i maestri disponibili. Se non selezioni nessun maestro, saranno considerati tutti disponibili.
            </p>
            {DAYS.map((d) => {
              const ds = config.day_settings?.[d.key] || { court_ids: courts.map((c) => c.id), coach_ids: [] };
              return (
                <Card key={d.key} className="card-warm">
                  <CardContent className="p-4">
                    <h4 className="font-bold text-slate-900 mb-2">{d.label}</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1.5 flex items-center gap-1">
                          <MapPin className="w-3 h-3" /> Campi
                        </p>
                        <div className="space-y-1.5">
                          {courts.map((court) => (
                            <label key={court.id} className="flex items-center gap-2 cursor-pointer text-sm">
                              <Checkbox
                                checked={(ds.court_ids || []).includes(court.id)}
                                onCheckedChange={() => toggleDayCourt(d.key, court.id)}
                                data-testid={`day-${d.key}-court-${court.id}`}
                              />
                              <span>{court.nome} <span className="text-xs text-slate-500">({court.tipo})</span></span>
                            </label>
                          ))}
                        </div>
                      </div>
                      <div>
                        <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1.5 flex items-center gap-1">
                          <Users className="w-3 h-3" /> Maestri disponibili
                        </p>
                        {coaches.length === 0 ? (
                          <p className="text-xs text-slate-400 italic">Aggiungi maestri prima</p>
                        ) : (
                          <div className="space-y-1.5">
                            {coaches.map((coach) => (
                              <label key={coach.id} className="flex items-center gap-2 cursor-pointer text-sm">
                                <Checkbox
                                  checked={(ds.coach_ids || []).includes(coach.id)}
                                  onCheckedChange={() => toggleDayCoach(d.key, coach.id)}
                                  data-testid={`day-${d.key}-coach-${coach.id}`}
                                />
                                <span>{coach.nome}</span>
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Per-court hours grid */}
                    <div className="mt-3 pt-3 border-t border-slate-200">
                      <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1">
                        <Clock className="w-3 h-3" /> Orari di apertura per campo (30 min)
                      </p>
                      <div className="space-y-2">
                        {courts.filter((c) => (ds.court_ids || []).includes(c.id)).map((court) => {
                          const chAll = ds.court_hours || {};
                          const list = chAll[court.id] ?? [...TIMES]; // undefined ⇒ all TIMES
                          const activeSet = new Set(list);
                          const allOn = list.length === TIMES.length;
                          return (
                            <div key={court.id} className="rounded-lg border border-slate-200 p-2 bg-white">
                              <div className="flex items-center justify-between mb-1.5">
                                <span className={cn(
                                  "text-xs font-bold px-2 py-0.5 rounded-full",
                                  court.tipo === "terra" ? "bg-orange-100 text-orange-800" : "bg-emerald-100 text-emerald-800"
                                )}>{court.nome}</span>
                                <button
                                  type="button"
                                  onClick={() => setDayCourtHoursAll(d.key, court.id, !allOn)}
                                  data-testid={`day-${d.key}-${court.id}-toggle-all`}
                                  className="text-[10px] text-slate-500 hover:text-slate-800 underline"
                                >
                                  {allOn ? "Deseleziona tutto" : "Seleziona tutto"}
                                </button>
                              </div>
                              <div className="grid grid-cols-5 sm:grid-cols-10 gap-1">
                                {TIMES.map((t) => {
                                  const on = activeSet.has(t);
                                  return (
                                    <button
                                      key={t}
                                      type="button"
                                      onClick={() => toggleDayCourtHour(d.key, court.id, t)}
                                      data-testid={`day-${d.key}-${court.id}-hour-${t}`}
                                      className={cn(
                                        "text-[10px] font-mono py-1 rounded border transition-colors",
                                        on
                                          ? "bg-emerald-500 text-white border-emerald-500"
                                          : "bg-slate-50 text-slate-400 border-slate-200 hover:bg-slate-100"
                                      )}
                                    >
                                      {t}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })}
                        {courts.filter((c) => (ds.court_ids || []).includes(c.id)).length === 0 && (
                          <p className="text-xs text-slate-400 italic">Seleziona prima almeno un campo per questa giornata</p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </TabsContent>

          {/* Groups */}
          <TabsContent value="groups" className="mt-4 space-y-3">
            {groups.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-6">
                Nessun gruppo definito. Crea prima un gruppo dalla dashboard.
              </p>
            ) : (
              groups.map((g) => (
                <Card key={g.id} className="card-warm">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="w-3 h-3 rounded-full" style={{ background: g.color || "#C85A32" }} />
                      <h4 className="font-bold text-slate-900">{g.nome}</h4>
                      <span className="text-xs text-slate-500">({g.registration_ids.length} iscritti)</span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <Label className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1 block">
                          Maestri
                        </Label>
                        {coaches.length === 0 ? (
                          <p className="text-xs text-slate-400 italic">Aggiungi maestri prima</p>
                        ) : (
                          <div className="space-y-1">
                            {coaches.map((coach) => (
                              <label key={coach.id} className="flex items-center gap-2 cursor-pointer text-sm">
                                <Checkbox
                                  checked={(g.coach_ids || []).includes(coach.id)}
                                  onCheckedChange={() => {
                                    const has = (g.coach_ids || []).includes(coach.id);
                                    const next = has
                                      ? (g.coach_ids || []).filter((c) => c !== coach.id)
                                      : [...(g.coach_ids || []), coach.id];
                                    updateGroupField(g, "coach_ids", next);
                                  }}
                                  data-testid={`group-${g.id}-coach-${coach.id}`}
                                />
                                <span>{coach.nome}</span>
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="space-y-2">
                        <div>
                          <Label className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1 block">
                            Allenamenti a settimana
                          </Label>
                          <Select
                            value={String(g.sessions_per_week ?? 1)}
                            onValueChange={(v) => updateGroupField(g, "sessions_per_week", Number(v))}
                          >
                            <SelectTrigger data-testid={`group-${g.id}-sessions`}><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="0">0 (macro-categoria)</SelectItem>
                              {[1, 2, 3, 4, 5].map((n) => (
                                <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {(g.sessions_per_week ?? 1) === 0 && (
                            <p className="text-[10px] text-indigo-600 mt-1">
                              Nessuno allenamento in calendario — usato come raggruppamento logico degli iscritti.
                            </p>
                          )}
                        </div>
                        {(g.sessions_per_week ?? 1) > 0 && (
                        <div>
                          <Label className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1 block">
                            Durata sessioni (min)
                          </Label>
                          <div className="space-y-1.5" data-testid={`group-${g.id}-durations`}>
                            {Array.from({ length: g.sessions_per_week ?? 1 }).map((_, si) => {
                              const durs = g.session_durations && g.session_durations.length
                                ? [...g.session_durations]
                                : Array(g.sessions_per_week ?? 1).fill(g.session_duration_minutes || 60);
                              // pad/trim to match sessions_per_week
                              while (durs.length < (g.sessions_per_week ?? 1)) durs.push(g.session_duration_minutes || 60);
                              const value = String(durs[si] ?? 60);
                              return (
                                <div key={si} className="flex items-center gap-2">
                                  <span className="text-[10px] font-mono text-slate-500 w-8">#{si + 1}</span>
                                  <Select
                                    value={value}
                                    onValueChange={(v) => {
                                      const next = durs.slice(0, g.sessions_per_week ?? 1);
                                      next[si] = Number(v);
                                      updateGroupField(g, "session_durations", next);
                                    }}
                                  >
                                    <SelectTrigger data-testid={`group-${g.id}-duration-${si}`} className="h-8">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="30">30 min</SelectItem>
                                      <SelectItem value="60">60 min</SelectItem>
                                      <SelectItem value="90">90 min</SelectItem>
                                      <SelectItem value="120">120 min</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>
                              );
                            })}
                          </div>
                          <p className="text-[10px] text-slate-400 mt-1">
                            Ogni sessione può avere una durata diversa
                          </p>
                        </div>
                        )}
                        <div>
                          <Label className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1 block">
                            Campo preferito
                          </Label>
                          <Select
                            value={g.preferred_court || "any"}
                            onValueChange={(v) => updateGroupField(g, "preferred_court", v === "any" ? null : v)}
                          >
                            <SelectTrigger data-testid={`group-${g.id}-court`}><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="any">Qualsiasi</SelectItem>
                              {courts.map((c) => (
                                <SelectItem key={c.id} value={c.id}>{c.nome} ({c.tipo})</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        {g.athletic_enabled && athleticTrainers.filter((t) => t.enabled !== false).length > 1 && (
                          <div>
                            <Label className="text-[11px] font-bold uppercase tracking-wider text-orange-700 mb-1 block flex items-center gap-1">
                              <Dumbbell className="w-3 h-3" /> Preparatore atletico
                            </Label>
                            <Select
                              value={g.preferred_trainer_id || "auto"}
                              onValueChange={(v) => updateGroupField(g, "preferred_trainer_id", v === "auto" ? null : v)}
                            >
                              <SelectTrigger data-testid={`group-${g.id}-trainer`}><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="auto">Automatico</SelectItem>
                                {athleticTrainers.filter((t) => t.enabled !== false).map((t) => (
                                  <SelectItem key={t.id} value={t.id}>
                                    <span className="flex items-center gap-2">
                                      <span className="w-2.5 h-2.5 rounded-full" style={{ background: t.color || "#F97316" }} />
                                      {t.nome}
                                    </span>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                        <div>
                          <Label className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1 block">
                            Colore
                          </Label>
                          <div className="flex flex-wrap gap-1.5" data-testid={`group-${g.id}-palette`}>
                            {COLORS.map((col) => {
                              const usedBy = groups.find(
                                (other) => other.id !== g.id && (other.color || "").toLowerCase() === col.toLowerCase()
                              );
                              const isCurrent = (g.color || "").toLowerCase() === col.toLowerCase();
                              const disabled = !!usedBy && !isCurrent;
                              return (
                                <button
                                  key={col}
                                  type="button"
                                  onClick={() => !disabled && updateGroupField(g, "color", col)}
                                  data-testid={`group-${g.id}-color-${col}`}
                                  title={disabled ? `Già usato da "${usedBy.nome}"` : col}
                                  disabled={disabled}
                                  className={cn(
                                    "relative w-6 h-6 rounded-full border-2 transition-transform",
                                    isCurrent
                                      ? "border-slate-900 scale-110 ring-2 ring-slate-300"
                                      : disabled
                                        ? "border-white opacity-30 cursor-not-allowed"
                                        : "border-white shadow hover:scale-110"
                                  )}
                                  style={{ background: col }}
                                >
                                  {disabled && (
                                    <span className="absolute inset-0 flex items-center justify-center text-white text-[10px] font-bold">×</span>
                                  )}
                                </button>
                              );
                            })}
                          </div>
                          <div className="flex items-center gap-2 mt-2">
                            <label
                              className="relative w-9 h-8 rounded-md border border-slate-300 overflow-hidden cursor-pointer shadow-sm hover:shadow"
                              title="Apri palette completa"
                              style={{ background: g.color || "#64748b" }}
                            >
                              <input
                                type="color"
                                value={g.color || "#64748b"}
                                onChange={(e) => updateGroupField(g, "color", e.target.value.toUpperCase())}
                                data-testid={`group-${g.id}-color-picker`}
                                className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
                              />
                            </label>
                            <Input
                              type="text"
                              value={g.color || ""}
                              placeholder="#RRGGBB"
                              onChange={(e) => {
                                const v = e.target.value.trim().toUpperCase();
                                if (/^#[0-9A-F]{0,6}$/.test(v)) {
                                  if (/^#[0-9A-F]{6}$/.test(v)) updateGroupField(g, "color", v);
                                  else updateGroupField(g, "color", v); // allow partial typing (backend will reject on save if incomplete)
                                }
                              }}
                              data-testid={`group-${g.id}-color-hex`}
                              className="h-8 w-28 font-mono text-xs uppercase"
                              maxLength={7}
                            />
                          </div>
                          <p className="text-[10px] text-slate-400 mt-1">
                            I colori con × sono già usati da un altro gruppo. Puoi anche scegliere qualsiasi colore dalla palette o inserire un codice esadecimale (es. <span className="font-mono">#3B82F6</span>).
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50/60 p-3">
                      <label className="flex items-start gap-2 cursor-pointer">
                        <Checkbox
                          checked={!!g.dual_court}
                          onCheckedChange={(v) => updateGroupField(g, "dual_court", !!v)}
                          data-testid={`group-${g.id}-dual-court`}
                          className="mt-0.5"
                        />
                        <div className="flex-1">
                          <div className="text-sm font-bold text-emerald-900 flex items-center gap-1.5">
                            <MapPin className="w-3.5 h-3.5" />
                            Doppio campo — usa Campo 1 + Campo 2 in parallelo
                          </div>
                          <p className="text-[10px] text-emerald-800/80 mt-0.5">
                            Il gruppo occupa contemporaneamente Campo 1 e Campo 2 (fisicamente adiacenti). Il campo preferito viene ignorato.
                          </p>
                        </div>
                      </label>
                    </div>
                    <div className="mt-3 rounded-lg border border-orange-200 bg-orange-50/60 p-3">
                      <label className="flex items-start gap-2 cursor-pointer">
                        <Checkbox
                          checked={!!g.athletic_enabled}
                          onCheckedChange={(v) => updateGroupField(g, "athletic_enabled", !!v)}
                          data-testid={`group-${g.id}-athletic-toggle`}
                          className="mt-0.5"
                        />
                        <div className="flex-1">
                          <div className="text-sm font-bold text-orange-900 flex items-center gap-1.5">
                            <Dumbbell className="w-3.5 h-3.5" />
                            Preparazione atletica (60 min in sequenza al tennis, Lun/Ven)
                          </div>
                          {g.athletic_enabled && (
                            <div className="mt-2">
                              <Label className="text-[11px] font-bold uppercase tracking-wider text-orange-800 mb-1 block">
                                Slot atletica condivisa
                              </Label>
                              <Input
                                value={g.athletic_shared_label || ""}
                                onChange={(e) => updateGroupField(g, "athletic_shared_label", e.target.value)}
                                placeholder="Es. 'A' — gruppi con stessa etichetta fanno atletica insieme"
                                data-testid={`group-${g.id}-athletic-label`}
                                className="h-8 text-sm bg-white"
                              />
                              <p className="text-[10px] text-orange-800/80 mt-1">
                                Lascia vuoto per atletica in slot separato. Gruppi con la stessa etichetta vengono piazzati nello stesso orario.
                              </p>
                            </div>
                          )}
                        </div>
                      </label>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>

          {/* Calendar */}
          <TabsContent value="calendar" className="mt-4 space-y-3">
            {/* Versions bar */}
            {(versions.length > 0 || schedule?.sessions?.length > 0) && (
              <Card className="border-slate-200">
                <CardContent className="p-3 flex flex-col sm:flex-row gap-2 items-start sm:items-center justify-between">
                  <div className="flex items-center gap-2 flex-wrap">
                    <History className="w-4 h-4 text-slate-500" />
                    <span className="text-xs font-bold uppercase tracking-wider text-slate-500">
                      Versioni ({versions.length})
                    </span>
                    <button
                      type="button"
                      onClick={() => setViewingVersion(null)}
                      data-testid="view-current-btn"
                      className={cn(
                        "text-xs font-semibold px-2.5 py-1 rounded-full border",
                        !viewingVersion
                          ? "bg-slate-900 text-white border-slate-900"
                          : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
                      )}
                    >
                      Corrente
                    </button>
                    {versions.map((v) => (
                      <div key={v.id} className="inline-flex items-center gap-0.5 rounded-full border border-slate-200 bg-white pl-2 pr-0.5">
                        <button
                          type="button"
                          onClick={() => setViewingVersion(v)}
                          data-testid={`view-version-${v.id}`}
                          className={cn(
                            "text-xs font-semibold py-1 pr-1.5",
                            viewingVersion?.id === v.id ? "text-[#C85A32]" : "text-slate-700"
                          )}
                        >
                          {v.nome}
                          <span className="text-[9px] font-mono text-slate-400 ml-1">
                            {new Date(v.saved_at).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit" })}
                          </span>
                        </button>
                        <button
                          type="button"
                          onClick={() => restoreVersion(v)}
                          data-testid={`restore-version-${v.id}`}
                          title="Ripristina come calendario corrente"
                          className="w-5 h-5 rounded-full hover:bg-emerald-50 text-emerald-600 flex items-center justify-center"
                        >
                          <RotateCcw className="w-3 h-3" />
                        </button>
                        <button
                          type="button"
                          onClick={() => deleteVersion(v)}
                          data-testid={`delete-version-${v.id}`}
                          title="Elimina versione"
                          className="w-5 h-5 rounded-full hover:bg-rose-50 text-rose-500 flex items-center justify-center"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    {versions.length >= 2 && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => { setCompareVersionId(versions[0]?.id || ""); setCompareOpen(true); }}
                        data-testid="compare-versions-btn"
                        className="gap-1.5"
                      >
                        <ArrowLeftRight className="w-3.5 h-3.5" /> Confronta
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setSaveVersionOpen(true)}
                      disabled={!schedule?.sessions?.length || hasEdits}
                      data-testid="save-version-btn"
                      className="gap-1.5 border-[#C85A32]/40 text-[#C85A32] hover:bg-[#C85A32]/10 hover:text-[#C85A32]"
                      title={hasEdits ? "Salva prima le modifiche" : "Snapshot del calendario corrente"}
                    >
                      <Bookmark className="w-3.5 h-3.5" /> Salva versione
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {viewingVersion && (
              <div className="rounded-xl bg-slate-50 border border-slate-200 px-3 py-2 text-xs text-slate-700 flex items-center justify-between gap-2 flex-wrap">
                <span>
                  📚 Stai visualizzando la versione <strong>&laquo;{viewingVersion.nome}&raquo;</strong> del{" "}
                  {new Date(viewingVersion.saved_at).toLocaleString("it-IT")} (sola lettura).
                </span>
                <Button size="sm" variant="ghost" onClick={() => setViewingVersion(null)} className="h-7 text-xs">
                  Torna a corrente
                </Button>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-2 items-start sm:items-center justify-between">
              <div className="text-xs text-slate-500">
                {schedule?.generated_at
                  ? `Ultima generazione: ${new Date(schedule.generated_at).toLocaleString("it-IT")}`
                  : "Nessun calendario generato"}
                {hasEdits && <span className="ml-2 text-amber-700 font-semibold">· Modifiche non salvate</span>}
              </div>
              <div className="flex flex-wrap gap-2">
                {hasEdits && (
                  <>
                    {undoStack.length > 0 && (
                      <Button size="sm" variant="outline" onClick={undoLast} data-testid="undo-last-btn" className="gap-1.5 border-indigo-300 text-indigo-700 hover:bg-indigo-50">
                        <RotateCcw className="w-3.5 h-3.5" /> Annulla ultimo ({undoStack.length})
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={discardEdits} data-testid="discard-edits-btn" className="gap-1.5 text-slate-600">
                      <RotateCcw className="w-3.5 h-3.5" /> Annulla modifiche
                    </Button>
                    <Button size="sm" onClick={saveEdits} disabled={savingSched} data-testid="save-schedule-btn" className="gap-1.5 bg-amber-600 hover:bg-amber-700">
                      <SaveIcon className="w-3.5 h-3.5" />
                      {savingSched ? "Salvataggio..." : "Salva modifiche"}
                    </Button>
                  </>
                )}
                {currentSessions.length > 0 && (
                  <>
                    <Button size="sm" variant="outline" onClick={exportPng} disabled={exporting} data-testid="export-png-btn" className="gap-1.5">
                      <ImageIcon className="w-3.5 h-3.5" /> PNG
                    </Button>
                    <Button size="sm" variant="outline" onClick={exportPdf} disabled={exporting} data-testid="export-pdf-btn" className="gap-1.5">
                      <FileDown className="w-3.5 h-3.5" /> PDF
                    </Button>
                  </>
                )}
                {!isReadOnly && (
                  <label className="flex items-center gap-1.5 text-[11px] text-slate-600 cursor-pointer select-none" title="Genera 5 varianti con priorità mescolate e sceglie la migliore. Rallenta di 2–3 secondi.">
                    <input type="checkbox" checked={multiRun} onChange={(e) => setMultiRun(e.target.checked)} className="accent-[#C85A32]" data-testid="multi-run-checkbox" />
                    Ricerca approfondita
                  </label>
                )}
                {!isReadOnly && (
                  <Button size="sm" variant="outline" onClick={runHealthCheck} data-testid="health-check-btn" className="gap-1.5 border-amber-400 text-amber-800 hover:bg-amber-50">
                    <AlertTriangle className="w-3.5 h-3.5" /> Controlla dati
                  </Button>
                )}
                {!isReadOnly && (
                  <Button onClick={() => generate({ multiRun })} disabled={generating} className="bg-[#C85A32] hover:bg-[#a94a28] gap-2" data-testid="generate-schedule-btn">
                    <Play className="w-4 h-4" />
                    {generating ? "Generazione..." : "Genera"}
                  </Button>
                )}
                {!isReadOnly && (schedule?.sessions?.length > 0 || (editedSessions && editedSessions.length > 0)) && missingSessions.length > 0 && (
                  <Button onClick={completeAuto} disabled={generating} variant="outline" className="border-emerald-600 text-emerald-700 hover:bg-emerald-50 gap-2" data-testid="complete-auto-btn">
                    <Sparkles className="w-4 h-4" />
                    Completa auto ({missingSessions.length})
                  </Button>
                )}
                {!isReadOnly && (
                  <Button onClick={startEmpty} variant="ghost" className="text-slate-600 hover:text-slate-800 gap-2 text-xs" data-testid="start-empty-btn">
                    <RotateCcw className="w-3.5 h-3.5" />
                    Svuota
                  </Button>
                )}
              </div>
            </div>

            {!isReadOnly && missingSessions.length > 0 && (
              <Card className="border-indigo-300 bg-indigo-50 sticky top-0 z-30 shadow-md backdrop-blur" data-testid="missing-pool">
                <CardContent className="p-3">
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <Bookmark className="w-4 h-4 text-indigo-700" />
                    <h4 className="text-sm font-bold text-indigo-900">Sessioni mancanti ({missingSessions.length})</h4>
                    <span className="text-[11px] text-indigo-700/80">Trascina un blocco negli slot vuoti del calendario</span>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {missingSessions.map((m, i) => {
                      if (m.kind === "athletic") {
                        const trainerId = m.group.preferred_trainer_id || athleticTrainers.find((t) => t.enabled !== false)?.id;
                        const tr = athleticTrainers.find((t) => t.id === trainerId);
                        const bg = tr?.color || "#F97316";
                        return (
                          <div
                            key={`ath-${m.groupId}`}
                            draggable
                            onDragStart={(e) => {
                              e.dataTransfer.setData("text/missing-athletic-group-id", m.groupId);
                              e.dataTransfer.effectAllowed = "copyMove";
                            }}
                            data-testid={`missing-athletic-brick-${m.groupId}`}
                            className="inline-flex items-center gap-1.5 rounded-md text-white text-xs font-semibold px-2 py-1 shadow-sm cursor-move hover:shadow-md transition-shadow border-2 border-dashed border-white/40"
                            style={{ background: bg }}
                            title={`Atletica · ${m.group.nome}${m.label ? ` · etichetta "${m.label}"` : ""} — trascina sulla colonna Atletica`}
                          >
                            <Dumbbell className="w-3 h-3" />
                            <span className="truncate max-w-[140px]">{m.group.nome}</span>
                            {m.label && (
                              <span className="text-[10px] font-mono bg-white/25 rounded px-1">{m.label}</span>
                            )}
                            <span className="text-[10px] font-mono opacity-90">60′</span>
                          </div>
                        );
                      }
                      return (
                        <div
                          key={`${m.groupId}-${m.index}`}
                          draggable
                          onDragStart={(e) => {
                            e.dataTransfer.setData("text/missing-group-id", m.groupId);
                            e.dataTransfer.setData("text/missing-duration", String(m.duration));
                            e.dataTransfer.effectAllowed = "copyMove";
                            setSuggestedSlots(computeSuggestions(m.groupId, m.duration));
                          }}
                          onDragEnd={() => setSuggestedSlots(new Set())}
                          data-testid={`missing-brick-${m.groupId}-${m.index}`}
                          className="inline-flex items-center gap-1.5 rounded-md text-white text-xs font-semibold px-2 py-1 shadow-sm cursor-move hover:shadow-md transition-shadow border-2 border-white/40"
                          style={{ background: m.group.color || "#64748b" }}
                          title={`${m.group.nome} — ${m.duration} min · trascina in uno slot (i vincoli non rispettati verranno segnalati sul blocco). Al trascinamento vengono suggeriti i 5 slot migliori (bordo verde).`}
                        >
                          {m.group.dual_court && <MapPin className="w-3 h-3" />}
                          <span className="truncate max-w-[140px]">{m.group.nome}</span>
                          <span className="text-[10px] font-mono opacity-90">{m.duration}′</span>
                        </div>
                      );
                    })}
                  </div>
                  <p className="text-[10px] text-indigo-800/70 mt-1.5">
                    ⚠ In manuale i vincoli sono soft: campo chiuso, doppio campo non disponibile, maestro impegnato, "impossibile" allievi vengono <b>segnalati sul blocco</b> senza bloccare l'inserimento. Se la durata non entra viene ridotta a 60 min. Solo la sovrapposizione fisica sullo stesso campo resta impedita.
                  </p>
                </CardContent>
              </Card>
            )}

            {currentCriticities.length > 0 && (
              <Card className="border-amber-300 bg-amber-50/60">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle className="w-4 h-4 text-amber-600" />
                    <h4 className="text-sm font-bold text-amber-900">Criticità rilevate ({currentCriticities.length})</h4>
                  </div>
                  <ul className="space-y-1 text-xs text-amber-900">
                    {currentCriticities.map((c, i) => (
                      <li key={i} className="flex gap-1.5">
                        <span className="text-amber-600">•</span>
                        <span>{c}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {(currentSessions.length > 0 || editedSessions !== null) && (
              <div ref={calendarRef}>
                <div className="flex items-center justify-end mb-2 gap-1">
                  <button
                    type="button"
                    onClick={() => setViewMode("perDay")}
                    data-testid="view-per-day-btn"
                    className={cn(
                      "text-xs font-semibold px-3 py-1 rounded-l-full border",
                      viewMode === "perDay"
                        ? "bg-slate-900 text-white border-slate-900"
                        : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                    )}
                  >
                    Per giorno
                  </button>
                  <button
                    type="button"
                    onClick={() => setViewMode("perCourt")}
                    data-testid="view-per-court-btn"
                    className={cn(
                      "text-xs font-semibold px-3 py-1 rounded-r-full border -ml-px",
                      viewMode === "perCourt"
                        ? "bg-slate-900 text-white border-slate-900"
                        : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                    )}
                  >
                    Per campo
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowAthletic((v) => !v)}
                    data-testid="toggle-athletic-btn"
                    className={cn(
                      "ml-2 text-xs font-semibold px-3 py-1 rounded-full border inline-flex items-center gap-1",
                      showAthletic
                        ? "bg-orange-500 text-white border-orange-500"
                        : "bg-white text-slate-500 border-slate-200 hover:bg-slate-50"
                    )}
                    title={showAthletic ? "Nascondi atletica" : "Mostra atletica"}
                  >
                    <Dumbbell className="w-3 h-3" /> Atletica {showAthletic ? "" : "off"}
                  </button>
                </div>
                {viewMode === "perDay" ? (
                  <CalendarGrid
                    sessions={displaySessions}
                    groups={groups}
                    courts={courts}
                    onFocus={setFocusSession}
                    onMove={isReadOnly ? null : moveSession}
                    onCreate={isReadOnly ? null : createSessionFromMissing}
                    onDelete={isReadOnly ? null : deleteSession}
                    onAthleticMove={isReadOnly ? null : moveAthleticBucket}
                    onAthleticCreate={isReadOnly ? null : createAthleticFromMissing}
                    onAthleticDelete={isReadOnly ? null : deleteAthleticBucket}
                    suggestedSlots={suggestedSlots}
                    showAthletic={showAthletic}
                    trainer={athleticTrainers.find((t) => t.id === (currentSessions.find((s) => s.type === "athletic")?.coach_ids?.[0])) || athleticTrainers.find((t) => t.enabled !== false)}
                    trainerById={Object.fromEntries(athleticTrainers.map((t) => [t.id, t]))}
                  />
                ) : (
                  <CourtsView
                    sessions={displaySessions}
                    groups={groups}
                    courts={courts}
                    onFocus={setFocusSession}
                    onMove={isReadOnly ? null : moveSession}
                    onCreate={isReadOnly ? null : createSessionFromMissing}
                    onDelete={isReadOnly ? null : deleteSession}
                    onAthleticMove={isReadOnly ? null : moveAthleticBucket}
                    onAthleticCreate={isReadOnly ? null : createAthleticFromMissing}
                    onAthleticDelete={isReadOnly ? null : deleteAthleticBucket}
                    suggestedSlots={suggestedSlots}
                    showAthletic={showAthletic}
                    trainer={athleticTrainers.find((t) => t.id === (currentSessions.find((s) => s.type === "athletic")?.coach_ids?.[0])) || athleticTrainers.find((t) => t.enabled !== false)}
                    trainerById={Object.fromEntries(athleticTrainers.map((t) => [t.id, t]))}
                  />
                )}
              </div>
            )}
            {!isReadOnly && (
              <p className="text-[11px] text-slate-500">
                💡 Trascina un blocco su un altro slot vuoto per spostarlo — inclusi i blocchi di <b>atletica</b> (sposta l'intero bucket). Passa il mouse sul badge ⚠ per leggere l'elenco dei conflitti. Le modifiche vanno salvate.
              </p>
            )}
          </TabsContent>
        </Tabs>

        {/* Save version dialog */}
        <Dialog open={saveVersionOpen} onOpenChange={setSaveVersionOpen}>
          <DialogContent className="max-w-md" data-testid="save-version-dialog">
            <DialogHeader>
              <DialogTitle>Salva versione del calendario</DialogTitle>
              <DialogDescription>
                Dai un nome a questo snapshot (es. "Ipotesi 1", "Con Marco lunedì", "Bozza 20/09") per confrontarlo in seguito.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={(e) => { e.preventDefault(); saveVersion(); }} className="space-y-4 mt-2">
              <div>
                <Label htmlFor="version-name" className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1.5 block">
                  Nome versione
                </Label>
                <Input
                  id="version-name"
                  data-testid="version-name-input"
                  value={newVersionName}
                  onChange={(e) => setNewVersionName(e.target.value)}
                  placeholder="Es. Ipotesi con Marco Lun"
                  autoFocus
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="ghost" onClick={() => setSaveVersionOpen(false)}>Annulla</Button>
                <Button
                  type="submit"
                  disabled={savingVersion || !newVersionName.trim()}
                  data-testid="confirm-save-version-btn"
                  className="bg-[#C85A32] hover:bg-[#a94a28]"
                >
                  {savingVersion ? "Salvataggio..." : "Salva versione"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Compare versions dialog */}
        <Dialog open={compareOpen} onOpenChange={setCompareOpen}>
          <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="compare-dialog">
            <DialogHeader>
              <DialogTitle>Confronta versioni</DialogTitle>
              <DialogDescription>
                Metti a confronto la versione salvata con il calendario corrente.
              </DialogDescription>
            </DialogHeader>
            <div className="mt-2 mb-3">
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1.5 block">
                Versione da confrontare
              </Label>
              <Select value={compareVersionId} onValueChange={setCompareVersionId}>
                <SelectTrigger data-testid="compare-select"><SelectValue placeholder="Scegli versione" /></SelectTrigger>
                <SelectContent>
                  {versions.map((v) => (
                    <SelectItem key={v.id} value={v.id}>
                      {v.nome} — {new Date(v.saved_at).toLocaleDateString("it-IT")}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <CompareDiff
              versionSessions={versions.find((v) => v.id === compareVersionId)?.sessions || []}
              currentSessions={schedule?.sessions || []}
              groups={groups}
              courts={courts}
            />
          </DialogContent>
        </Dialog>

        {/* Health-check dialog */}
        <Dialog open={healthOpen} onOpenChange={setHealthOpen}>
          <DialogContent className="max-w-lg" data-testid="health-check-dialog">
            <DialogHeader>
              <DialogTitle>Controllo qualità dei dati</DialogTitle>
              <DialogDescription>
                Verifica preferenze mancanti, gruppi senza maestro, atletica senza preparatore prima di generare.
              </DialogDescription>
            </DialogHeader>
            {healthReport && (
              <div className="space-y-3 text-sm">
                <div className={cn("rounded-lg p-3 border", healthReport.ok ? "bg-emerald-50 border-emerald-300 text-emerald-900" : "bg-red-50 border-red-300 text-red-900")}>
                  {healthReport.ok ? "✅ Dati pronti: nessun blocco. Puoi generare." : `⛔ ${healthReport.blockers.length} blocco/i critici — vanno risolti prima di generare.`}
                </div>
                {healthReport.blockers?.length > 0 && (
                  <div>
                    <h4 className="text-xs font-bold uppercase text-red-800 mb-1">Blocchi ({healthReport.blockers.length})</h4>
                    <ul className="text-xs space-y-1 text-red-900 list-disc pl-4">
                      {healthReport.blockers.map((w, i) => <li key={i}>{w}</li>)}
                    </ul>
                  </div>
                )}
                {healthReport.warnings?.length > 0 && (
                  <div>
                    <h4 className="text-xs font-bold uppercase text-amber-800 mb-1">Avvisi ({healthReport.warnings.length})</h4>
                    <ul className="text-xs space-y-1 text-amber-900 list-disc pl-4 max-h-48 overflow-y-auto">
                      {healthReport.warnings.map((w, i) => <li key={i}>{w}</li>)}
                    </ul>
                  </div>
                )}
                <div className="grid grid-cols-3 gap-2 text-[11px]">
                  <div className="rounded-md bg-slate-100 p-2"><b>{healthReport.stats?.registrations || 0}</b> iscritti</div>
                  <div className="rounded-md bg-slate-100 p-2"><b>{healthReport.stats?.groups || 0}</b> gruppi</div>
                  <div className="rounded-md bg-slate-100 p-2"><b>{healthReport.stats?.regs_no_preferences || 0}</b> senza preferenze</div>
                </div>
                <div className="flex justify-end gap-2 pt-2 border-t">
                  <Button variant="ghost" onClick={() => setHealthOpen(false)}>Chiudi</Button>
                  <Button
                    onClick={() => { setHealthOpen(false); generate({ multiRun }); }}
                    disabled={!healthReport.ok || generating}
                    className="bg-[#C85A32] hover:bg-[#a94a28]"
                    data-testid="health-check-generate-btn"
                  >
                    Genera comunque
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Session detail */}
        <Dialog open={!!focusSession} onOpenChange={(o) => !o && setFocusSession(null)}>
          <DialogContent className="max-w-md" data-testid="session-detail-dialog">
            {focusSession && (
              <SessionDetail
                session={focusSession}
                groups={groups}
                registrations={registrations}
                coaches={coaches}
                courts={courts}
                trainers={athleticTrainers}
                allSessions={displaySessions}
                config={config}
                onToggleExpected={!isReadOnly && !readOnly ? toggleExpected : null}
              />
            )}
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
}

function CompareDiff({ versionSessions, currentSessions, groups, courts }) {
  const gmap = useMemo(() => Object.fromEntries(groups.map((g) => [g.id, g])), [groups]);
  const cmap = useMemo(() => Object.fromEntries(courts.map((c) => [c.id, c])), [courts]);
  const keyOf = (s) => `${s.group_id}|${s.day}|${s.start_time}|${s.court_id}`;

  const versionKeys = new Set(versionSessions.map(keyOf));
  const currentKeys = new Set(currentSessions.map(keyOf));

  // For a group, show what changed: sessions only in version, only in current, unchanged
  const groupIds = new Set([...versionSessions, ...currentSessions].map((s) => s.group_id));
  const rows = [];
  groupIds.forEach((gid) => {
    const g = gmap[gid];
    const inV = versionSessions.filter((s) => s.group_id === gid);
    const inC = currentSessions.filter((s) => s.group_id === gid);
    rows.push({ g, inV, inC });
  });

  const label = (s) => {
    const dLabel = DAYS.find((x) => x.key === s.day)?.short || s.day;
    return `${dLabel} ${s.start_time}–${s.end_time} · ${cmap[s.court_id]?.nome || s.court_id}`;
  };

  return (
    <div className="space-y-3" data-testid="compare-diff">
      {rows.length === 0 ? (
        <p className="text-sm text-slate-500 italic">Nessuna sessione da confrontare</p>
      ) : (
        rows.map(({ g, inV, inC }) => (
          <div key={g?.id || Math.random()} className="rounded-lg border border-slate-200 bg-white p-3">
            <div className="flex items-center gap-2 mb-2">
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: g?.color || "#64748b" }} />
              <h4 className="font-bold text-slate-900">{g?.nome || "?"}</h4>
            </div>
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Versione salvata</div>
                {inV.length === 0 ? (
                  <p className="text-slate-400 italic">Nessuna</p>
                ) : (
                  <ul className="space-y-1">
                    {inV.map((s, i) => {
                      const same = currentKeys.has(keyOf(s));
                      return (
                        <li key={i} className={cn("px-2 py-1 rounded", same ? "bg-slate-50 text-slate-600" : "bg-rose-50 text-rose-800 line-through")}>
                          {label(s)}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
              <div>
                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Calendario corrente</div>
                {inC.length === 0 ? (
                  <p className="text-slate-400 italic">Nessuna</p>
                ) : (
                  <ul className="space-y-1">
                    {inC.map((s, i) => {
                      const same = versionKeys.has(keyOf(s));
                      return (
                        <li key={i} className={cn("px-2 py-1 rounded", same ? "bg-slate-50 text-slate-600" : "bg-emerald-50 text-emerald-800 font-semibold")}>
                          {label(s)}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>
          </div>
        ))
      )}
    </div>
  );
}

function CalendarGrid({ sessions, groups, courts, onFocus, onMove, onCreate, onDelete, onAthleticMove, onAthleticCreate, onAthleticDelete, suggestedSlots, showAthletic, trainer, trainerById }) {
  const gmap = useMemo(() => Object.fromEntries(groups.map((g) => [g.id, g])), [groups]);
  const tennisSessions = useMemo(() => sessions.filter((s) => s.type !== "athletic"), [sessions]);
  const athleticByKey = useMemo(() => groupAthletic(sessions), [sessions]);
  const nCourtCols = courts.length;

  const handleDragStart = (e, sessionIdx) => {
    e.dataTransfer.setData("text/session-idx", String(sessionIdx));
    e.dataTransfer.effectAllowed = "move";
  };
  const handleAthleticDragStart = (e, day, time) => {
    e.dataTransfer.setData("text/athletic-bucket", `${day}_${time}`);
    e.dataTransfer.effectAllowed = "move";
  };
  const handleDragEnd = () => {};

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = (e, day, time, court) => {
    e.preventDefault();
    // Missing-brick drop (create new session)
    const missingGid = e.dataTransfer.getData("text/missing-group-id");
    if (missingGid && onCreate) {
      const durStr = e.dataTransfer.getData("text/missing-duration");
      const duration = durStr ? Number(durStr) : undefined;
      onCreate(missingGid, day, time, court, duration);
      return;
    }
    const idxStr = e.dataTransfer.getData("text/session-idx");
    if (!idxStr) return;
    const idx = Number(idxStr);
    if (Number.isNaN(idx)) return;
    onMove && onMove(idx, day, time, court);
  };

  const handleAthleticDrop = (e, newDay, newTime) => {
    e.preventDefault();
    const missingAthGid = e.dataTransfer.getData("text/missing-athletic-group-id");
    if (missingAthGid && onAthleticCreate) {
      onAthleticCreate(missingAthGid, newDay, newTime);
      return;
    }
    const bucketKey = e.dataTransfer.getData("text/athletic-bucket");
    if (!bucketKey || !onAthleticMove) return;
    const [oldDay, oldTime] = bucketKey.split("_");
    onAthleticMove(oldDay, oldTime, newDay, newTime);
  };

  const gridTemplate = showAthletic
    ? `60px repeat(${nCourtCols}, minmax(0, 1fr)) 1.2fr`
    : `60px repeat(${nCourtCols}, minmax(0, 1fr))`;
  const athleticColIdx = showAthletic ? nCourtCols + 2 : null;

  return (
    <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white p-3 card-warm">
      <div className="min-w-[900px]">
        {DAYS.map((d) => (
          <div key={d.key} className="mb-4">
            <h3 className="text-sm font-bold text-slate-800 mb-2 uppercase tracking-wider">
              {d.label}
            </h3>
            <div
              className="grid gap-1"
              style={{
                gridTemplateColumns: gridTemplate,
                gridTemplateRows: `20px repeat(${TIMES.length}, 28px)`,
              }}
            >
              <div style={{ gridColumn: 1, gridRow: 1 }} />
              {courts.map((c, ci) => (
                <div
                  key={`hdr-${c.id}`}
                  style={{ gridColumn: ci + 2, gridRow: 1 }}
                  className="text-[10px] font-bold uppercase text-center text-slate-500"
                >
                  {c.nome} <span className="font-normal">({c.tipo})</span>
                </div>
              ))}
              {showAthletic && (
                <div
                  style={{ gridColumn: athleticColIdx, gridRow: 1 }}
                  className="text-[10px] font-bold uppercase text-center text-orange-700 flex items-center justify-center gap-1"
                >
                  <Dumbbell className="w-3 h-3" /> Atletica{trainer ? ` · ${trainer.nome}` : ""}
                </div>
              )}
              {TIMES.map((t, ti) => (
                <div
                  key={`tl-${d.key}-${t}`}
                  style={{ gridColumn: 1, gridRow: ti + 2 }}
                  className="text-[10px] font-mono text-slate-400 text-right pr-1 flex items-center justify-end"
                >
                  {t}
                </div>
              ))}
              {courts.map((c, ci) =>
                TIMES.map((t, ti) => {
                  const sIdx = tennisSessions.findIndex(
                    (x) => x.day === d.key && x.court_id === c.id && x.start_time === t
                  );
                  const s = sIdx >= 0 ? tennisSessions[sIdx] : null;
                  if (s) {
                    // find original index in the full sessions array for drag
                    const fullIdx = sessions.indexOf(s);
                    const g = gmap[s.group_id];
                    const slots = timeToSlots(s.start_time, s.end_time);
                    // Dual court: block starts at campo1 (ci=0) and spans 2 court columns
                    const spanCols = s.dual_court && c.id === "campo1" ? 2 : 1;
                    return (
                      <button
                        type="button"
                        key={`${d.key}-${c.id}-${t}-block`}
                        draggable={!!onMove}
                        onDragStart={(e) => handleDragStart(e, fullIdx)}
                        onDragEnd={handleDragEnd}
                        onClick={() => onFocus(s)}
                        data-testid={`session-block-${d.key}-${c.id}-${t}`}
                        className="rounded-md text-white text-xs font-semibold p-1.5 shadow-sm hover:shadow-md transition-shadow text-left cursor-move overflow-hidden"
                        style={{
                          background: g?.color || "#64748b",
                          gridColumn: spanCols > 1 ? `${ci + 2} / span ${spanCols}` : ci + 2,
                          gridRow: `${ti + 2} / span ${slots}`,
                        }}
                      >
                        <div className="font-bold truncate flex items-center gap-1">
                          {s.dual_court && <MapPin className="w-3 h-3 shrink-0" />}
                          {g?.nome || "?"}
                          {onDelete && (
                            <span
                              role="button"
                              onClick={(e) => { e.stopPropagation(); onDelete(fullIdx); }}
                              data-testid={`session-delete-${d.key}-${c.id}-${t}`}
                              title="Rimuovi sessione dal calendario"
                              className="ml-auto w-4 h-4 rounded-full bg-white/25 hover:bg-white/50 flex items-center justify-center text-[10px] shrink-0 cursor-pointer"
                            >×</span>
                          )}
                        </div>
                        <div className="text-[9px] font-mono opacity-90">
                          {s.start_time}–{s.end_time}
                          {s.dual_court && <span className="ml-1 opacity-80">· Campo 1+2</span>}
                        </div>
                        {s.conflicts?.length > 0 && (
                          <div
                            className="text-[9px] mt-0.5 bg-white/25 rounded px-1 inline-block cursor-help"
                            title={s.conflicts.map((c) => `• ${c}`).join("\n")}
                          >
                            ⚠ {s.conflicts.length}
                          </div>
                        )}
                      </button>
                    );
                  }
                  // A dual-court session on campo1 also covers campo2 at those slots
                  const coveredByDual = c.id === "campo2" && tennisSessions.some(
                    (x) =>
                      x.day === d.key &&
                      x.dual_court &&
                      x.court_id === "campo1" &&
                      x.start_time <= t &&
                      x.end_time > t
                  );
                  if (coveredByDual) return null;
                  const covered = tennisSessions.some(
                    (x) =>
                      x.day === d.key &&
                      x.court_id === c.id &&
                      x.start_time < t &&
                      x.end_time > t
                  );
                  if (covered) {
                    // Overlay drop-zone SEMPRE presente (trasparente) sopra il blocco,
                    // così è possibile spostare una sessione di 30 min "dentro" la sua stessa fascia
                    // senza dover mantenere uno stato React (che causava re-render e cancellazione del drag).
                    return (
                      <div
                        key={`${d.key}-${c.id}-${t}-overlay`}
                        onDragOver={handleDragOver}
                        onDrop={(e) => handleDrop(e, d.key, t, c.id)}
                        data-testid={`covered-slot-${d.key}-${c.id}-${t}`}
                        style={{ gridColumn: ci + 2, gridRow: ti + 2, zIndex: 20 }}
                        className="rounded-md bg-transparent hover:bg-sky-300/40 hover:border-2 hover:border-dashed hover:border-sky-500 transition-colors"
                      />
                    );
                  }
                  return (
                    <div
                      key={`${d.key}-${c.id}-${t}-empty`}
                      onDragOver={handleDragOver}
                      onDrop={(e) => handleDrop(e, d.key, t, c.id)}
                      data-testid={`empty-slot-${d.key}-${c.id}-${t}`}
                      style={{ gridColumn: ci + 2, gridRow: ti + 2 }}
                      className={cn(
                        "rounded-md bg-slate-50 border border-slate-100 hover:bg-slate-100 hover:border-slate-300 transition-colors",
                        suggestedSlots?.has?.(`${d.key}_${c.id}_${t}`) && "ring-2 ring-emerald-400 bg-emerald-50 border-emerald-300"
                      )}
                    />
                  );
                })
              )}
              {showAthletic &&
                TIMES.map((t, ti) => {
                  const key = `${d.key}_${t}`;
                  const bucket = athleticByKey[key];
                  if (bucket && bucket.length > 0) {
                    const first = bucket[0];
                    const slots = timeToSlots(first.start_time, first.end_time);
                    const label = first.athletic_label;
                    return (
                      <AthleticBlock
                        key={`ath-${d.key}-${t}`}
                        bucket={bucket}
                        groups={gmap}
                        slots={slots}
                        col={athleticColIdx}
                        row={ti + 2}
                        onFocus={onFocus}
                        label={label}
                        trainerById={trainerById}
                        testId={`athletic-block-${d.key}-${t}`}
                        draggable={!!onAthleticMove}
                        onDragStart={(e) => handleAthleticDragStart(e, d.key, t)}
                        onDragEnd={handleDragEnd}
                        onDragOver={onAthleticMove || onAthleticCreate ? handleDragOver : undefined}
                        onDrop={onAthleticMove || onAthleticCreate ? (e) => handleAthleticDrop(e, d.key, t) : undefined}
                        onDelete={onAthleticDelete ? () => onAthleticDelete(d.key, t) : undefined}
                      />
                    );
                  }
                  const covered = Object.entries(athleticByKey).some(([k, arr]) => {
                    const [ad, atime] = k.split("_");
                    if (ad !== d.key) return false;
                    const s0 = arr[0];
                    return atime < t && s0.end_time > t;
                  });
                  if (covered) return null;
                  return (
                    <div
                      key={`ath-empty-${d.key}-${t}`}
                      style={{ gridColumn: athleticColIdx, gridRow: ti + 2 }}
                      onDragOver={onAthleticMove ? handleDragOver : undefined}
                      onDrop={onAthleticMove ? (e) => handleAthleticDrop(e, d.key, t) : undefined}
                      data-testid={`athletic-empty-${d.key}-${t}`}
                      className="rounded-md bg-orange-50/40 border border-dashed border-orange-100 hover:bg-orange-100/60 transition-colors"
                    />
                  );
                })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function AthleticBlock({ bucket, groups, slots, col, row, onFocus, label, testId, trainerById, draggable, onDragStart, onDragEnd, onDragOver, onDrop, onDelete }) {
  // bucket = list of athletic sessions in the same slot; each has group_id
  const gs = bucket.map((s) => ({ session: s, group: groups[s.group_id] })).filter((x) => x.group);
  const first = bucket[0];
  const trainer = trainerById ? trainerById[first.coach_ids?.[0]] : null;
  const trainerColor = trainer?.color || "#F97316";
  const conflicts = first.conflicts || [];
  return (
    <button
      type="button"
      onClick={() => onFocus(first)}
      data-testid={testId}
      draggable={!!draggable}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragOver={onDragOver}
      onDrop={onDrop}
      className={cn(
        "rounded-md text-white text-xs font-semibold p-1.5 shadow-sm hover:shadow-md transition-shadow text-left overflow-hidden border-2 border-dashed border-white/40",
        draggable && "cursor-move"
      )}
      style={{
        gridColumn: col,
        gridRow: `${row} / span ${slots}`,
        background: trainerColor,
      }}
    >
      <div className="flex items-center gap-1 mb-0.5">
        <Dumbbell className="w-3 h-3 shrink-0" />
        <span className="text-[9px] font-mono opacity-90">
          {first.start_time}–{first.end_time}
        </span>
        {label && (
          <span className="text-[9px] font-mono bg-white/25 rounded px-1 ml-auto">
            {label}
          </span>
        )}
        {onDelete && (
          <span
            role="button"
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
            data-testid={`${testId}-delete`}
            title="Rimuovi blocco atletica"
            className={cn(
              "w-4 h-4 rounded-full bg-white/25 hover:bg-white/50 flex items-center justify-center text-[10px] shrink-0 cursor-pointer",
              !label && "ml-auto"
            )}
          >×</span>
        )}
      </div>
      <div className="space-y-0.5">
        {gs.map(({ group, session }) => (
          <div key={group.id} className="flex items-center gap-1 truncate rounded hover:bg-white/20"
            role="button" title="Apri questo gruppo"
            onClick={(e) => { e.stopPropagation(); onFocus(session); }}
            data-testid={`${testId}-group-${group.id}`}>
            <span
              className="inline-block w-1.5 h-1.5 rounded-full shrink-0"
              style={{ background: group.color || "#fff" }}
            />
            <span className="truncate">{group.nome}</span>
          </div>
        ))}
      </div>
      {conflicts.length > 0 && (
        <div
          className="text-[9px] mt-0.5 bg-white/25 rounded px-1 inline-block cursor-help"
          title={conflicts.map((c) => `• ${c}`).join("\n")}
        >
          ⚠ {conflicts.length}
        </div>
      )}
      {trainer && (
        <div className="mt-1 text-[9px] opacity-90 truncate">
          {trainer.nome}
        </div>
      )}
    </button>
  );
}

function timeToSlots(start, end) {
  const [sh, sm] = start.split(":").map(Number);
  const [eh, em] = end.split(":").map(Number);
  return ((eh * 60 + em) - (sh * 60 + sm)) / 30;
}

function addMin(t, mins) {
  const [h, m] = t.split(":").map(Number);
  const total = h * 60 + m + mins;
  const H = Math.floor(total / 60).toString().padStart(2, "0");
  const M = (total % 60).toString().padStart(2, "0");
  return `${H}:${M}`;
}

/**
 * Group athletic sessions by day+start_time into a map: `${day}_${time}` -> [sessions...]
 * Different groups sharing the same time slot (same athletic_label) are aggregated together.
 */
function groupAthletic(sessions) {
  const out = {};
  for (const s of sessions) {
    if (s.type !== "athletic") continue;
    const key = `${s.day}_${s.start_time}`;
    (out[key] = out[key] || []).push(s);
  }
  return out;
}

function CourtsView({ sessions, groups, courts, onFocus, onMove, onCreate, onDelete, onAthleticMove, onAthleticCreate, onAthleticDelete, suggestedSlots, showAthletic, trainer, trainerById }) {
  const gmap = useMemo(() => Object.fromEntries(groups.map((g) => [g.id, g])), [groups]);
  const tennisSessions = useMemo(() => sessions.filter((s) => s.type !== "athletic"), [sessions]);
  const athleticByKey = useMemo(() => groupAthletic(sessions), [sessions]);

  const handleDragStart = (e, sessionIdx) => {
    e.dataTransfer.setData("text/session-idx", String(sessionIdx));
    e.dataTransfer.effectAllowed = "move";
  };
  const handleAthleticDragStart = (e, day, time) => {
    e.dataTransfer.setData("text/athletic-bucket", `${day}_${time}`);
    e.dataTransfer.effectAllowed = "move";
  };
  const handleDragEnd = () => {};
  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };
  const handleDrop = (e, day, time, court) => {
    e.preventDefault();
    const missingGid = e.dataTransfer.getData("text/missing-group-id");
    if (missingGid && onCreate) {
      const durStr = e.dataTransfer.getData("text/missing-duration");
      const duration = durStr ? Number(durStr) : undefined;
      onCreate(missingGid, day, time, court, duration);
      return;
    }
    const idxStr = e.dataTransfer.getData("text/session-idx");
    if (!idxStr) return;
    const idx = Number(idxStr);
    if (Number.isNaN(idx)) return;
    onMove && onMove(idx, day, time, court);
  };
  const handleAthleticDrop = (e, newDay, newTime) => {
    e.preventDefault();
    const missingAthGid = e.dataTransfer.getData("text/missing-athletic-group-id");
    if (missingAthGid && onAthleticCreate) {
      onAthleticCreate(missingAthGid, newDay, newTime);
      return;
    }
    const bucketKey = e.dataTransfer.getData("text/athletic-bucket");
    if (!bucketKey || !onAthleticMove) return;
    const [oldDay, oldTime] = bucketKey.split("_");
    onAthleticMove(oldDay, oldTime, newDay, newTime);
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-3 card-warm space-y-6">
      {courts.map((c) => (
        <div key={c.id}>
          <div className="flex items-center gap-2 mb-2">
            <span
              className={cn(
                "inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded-full",
                c.tipo === "terra"
                  ? "bg-orange-100 text-orange-800"
                  : "bg-emerald-100 text-emerald-800"
              )}
            >
              <MapPin className="w-3 h-3" />
              {c.nome}
              <span className="font-normal opacity-80">({c.tipo})</span>
            </span>
          </div>
          <div className="overflow-x-auto">
            <div
              className="grid gap-1 min-w-[720px]"
              style={{
                gridTemplateColumns: "60px repeat(5, minmax(0, 1fr))",
                gridTemplateRows: `24px repeat(${TIMES.length}, 28px)`,
              }}
            >
              <div style={{ gridColumn: 1, gridRow: 1 }} />
              {DAYS.map((d, di) => (
                <div
                  key={`ch-${c.id}-${d.key}`}
                  style={{ gridColumn: di + 2, gridRow: 1 }}
                  className="text-[10px] font-bold uppercase text-center text-slate-500 flex items-center justify-center"
                >
                  {d.label}
                </div>
              ))}
              {TIMES.map((t, ti) => (
                <div
                  key={`ct-${c.id}-${t}`}
                  style={{ gridColumn: 1, gridRow: ti + 2 }}
                  className="text-[10px] font-mono text-slate-400 text-right pr-1 flex items-center justify-end"
                >
                  {t}
                </div>
              ))}
              {DAYS.map((d, di) =>
                TIMES.map((t, ti) => {
                  const sIdx = tennisSessions.findIndex(
                    (x) => x.day === d.key && x.court_id === c.id && x.start_time === t
                  );
                  const s = sIdx >= 0 ? tennisSessions[sIdx] : null;
                  // If this is campo2 and a dual_court session is on campo1 covering this slot, show linked marker
                  if (!s && c.id === "campo2") {
                    const dualPair = tennisSessions.find(
                      (x) => x.day === d.key && x.dual_court && x.court_id === "campo1" && x.start_time === t
                    );
                    if (dualPair) {
                      const g = gmap[dualPair.group_id];
                      const slots = timeToSlots(dualPair.start_time, dualPair.end_time);
                      return (
                        <div
                          key={`courtview-${c.id}-${d.key}-${t}-dual`}
                          className="rounded-md text-white text-xs font-semibold p-1.5 shadow-sm border-2 border-dashed border-white/60 overflow-hidden"
                          style={{
                            background: g?.color || "#64748b",
                            gridColumn: di + 2,
                            gridRow: `${ti + 2} / span ${slots}`,
                            opacity: 0.65,
                          }}
                        >
                          <div className="font-bold truncate leading-tight flex items-center gap-1">
                            <MapPin className="w-3 h-3 shrink-0" />
                            {g?.nome || "?"}
                          </div>
                          <div className="text-[9px] font-mono opacity-90">
                            ← Campo 1+2
                          </div>
                        </div>
                      );
                    }
                  }
                  if (s) {
                    const fullIdx = sessions.indexOf(s);
                    const g = gmap[s.group_id];
                    const slots = timeToSlots(s.start_time, s.end_time);
                    return (
                      <button
                        type="button"
                        key={`courtview-${c.id}-${d.key}-${t}-block`}
                        draggable={!!onMove}
                        onDragStart={(e) => handleDragStart(e, fullIdx)}
                        onDragEnd={handleDragEnd}
                        onClick={() => onFocus(s)}
                        data-testid={`courtview-block-${c.id}-${d.key}-${t}`}
                        className="rounded-md text-white text-xs font-semibold p-1.5 shadow-sm hover:shadow-md transition-shadow text-left cursor-move overflow-hidden"
                        style={{
                          background: g?.color || "#64748b",
                          gridColumn: di + 2,
                          gridRow: `${ti + 2} / span ${slots}`,
                        }}
                      >
                        <div className="font-bold truncate leading-tight">{g?.nome || "?"}</div>
                        <div className="text-[9px] font-mono opacity-90">
                          {s.start_time}–{s.end_time}
                        </div>
                        {s.conflicts?.length > 0 && (
                          <div
                            className="text-[9px] mt-0.5 bg-white/25 rounded px-1 inline-block cursor-help"
                            title={s.conflicts.map((c) => `• ${c}`).join("\n")}
                          >
                            ⚠ {s.conflicts.length}
                          </div>
                        )}
                      </button>
                    );
                  }
                  const covered = tennisSessions.some(
                    (x) =>
                      x.day === d.key &&
                      x.court_id === c.id &&
                      x.start_time < t &&
                      x.end_time > t
                  );
                  if (covered) {
                    return (
                      <div
                        key={`courtview-${c.id}-${d.key}-${t}-overlay`}
                        onDragOver={handleDragOver}
                        onDrop={(e) => handleDrop(e, d.key, t, c.id)}
                        data-testid={`courtview-covered-${c.id}-${d.key}-${t}`}
                        style={{ gridColumn: di + 2, gridRow: ti + 2, zIndex: 20 }}
                        className="rounded-md bg-transparent hover:bg-sky-300/40 hover:border-2 hover:border-dashed hover:border-sky-500 transition-colors"
                      />
                    );
                  }
                  return (
                    <div
                      key={`courtview-${c.id}-${d.key}-${t}-empty`}
                      onDragOver={handleDragOver}
                      onDrop={(e) => handleDrop(e, d.key, t, c.id)}
                      data-testid={`courtview-empty-${c.id}-${d.key}-${t}`}
                      style={{ gridColumn: di + 2, gridRow: ti + 2 }}
                      className={cn(
                        "rounded-md bg-slate-50 border border-slate-100 hover:bg-slate-100 hover:border-slate-300 transition-colors",
                        suggestedSlots?.has?.(`${d.key}_${c.id}_${t}`) && "ring-2 ring-emerald-400 bg-emerald-50 border-emerald-300"
                      )}
                    />
                  );
                })
              )}
            </div>
          </div>
        </div>
      ))}

      {showAthletic && (
        <div>
          <div className="flex items-center gap-2 mb-2">
            <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded-full bg-orange-500 text-white">
              <Dumbbell className="w-3 h-3" />
              Atletica
              {trainer && <span className="font-normal opacity-90">· {trainer.nome}</span>}
            </span>
          </div>
          <div className="overflow-x-auto">
            <div
              className="grid gap-1 min-w-[720px]"
              style={{
                gridTemplateColumns: "60px repeat(5, minmax(0, 1fr))",
                gridTemplateRows: `24px repeat(${TIMES.length}, 28px)`,
              }}
            >
              <div style={{ gridColumn: 1, gridRow: 1 }} />
              {DAYS.map((d, di) => (
                <div
                  key={`ath-h-${d.key}`}
                  style={{ gridColumn: di + 2, gridRow: 1 }}
                  className="text-[10px] font-bold uppercase text-center text-slate-500 flex items-center justify-center"
                >
                  {d.label}
                </div>
              ))}
              {TIMES.map((t, ti) => (
                <div
                  key={`ath-t-${t}`}
                  style={{ gridColumn: 1, gridRow: ti + 2 }}
                  className="text-[10px] font-mono text-slate-400 text-right pr-1 flex items-center justify-end"
                >
                  {t}
                </div>
              ))}
              {DAYS.map((d, di) =>
                TIMES.map((t, ti) => {
                  const key = `${d.key}_${t}`;
                  const bucket = athleticByKey[key];
                  if (bucket && bucket.length > 0) {
                    const first = bucket[0];
                    const slots = timeToSlots(first.start_time, first.end_time);
                    return (
                      <AthleticBlock
                        key={`courtview-ath-${d.key}-${t}`}
                        bucket={bucket}
                        groups={gmap}
                        slots={slots}
                        col={di + 2}
                        row={ti + 2}
                        onFocus={onFocus}
                        label={first.athletic_label}
                        trainerById={trainerById}
                        testId={`courtview-athletic-${d.key}-${t}`}
                        draggable={!!onAthleticMove}
                        onDragStart={(e) => handleAthleticDragStart(e, d.key, t)}
                        onDragEnd={handleDragEnd}
                        onDragOver={onAthleticMove || onAthleticCreate ? handleDragOver : undefined}
                        onDrop={onAthleticMove || onAthleticCreate ? (e) => handleAthleticDrop(e, d.key, t) : undefined}
                        onDelete={onAthleticDelete ? () => onAthleticDelete(d.key, t) : undefined}
                      />
                    );
                  }
                  const covered = Object.entries(athleticByKey).some(([k, arr]) => {
                    const [ad, atime] = k.split("_");
                    if (ad !== d.key) return false;
                    const s0 = arr[0];
                    return atime < t && s0.end_time > t;
                  });
                  if (covered) return null;
                  return (
                    <div
                      key={`courtview-ath-empty-${d.key}-${t}`}
                      style={{ gridColumn: di + 2, gridRow: ti + 2 }}
                      onDragOver={onAthleticMove ? handleDragOver : undefined}
                      onDrop={onAthleticMove ? (e) => handleAthleticDrop(e, d.key, t) : undefined}
                      data-testid={`courtview-athletic-empty-${d.key}-${t}`}
                      className="rounded-md bg-orange-50/40 border border-dashed border-orange-100 hover:bg-orange-100/60 transition-colors"
                    />
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function indexToEndTime(startIdx, slots) {
  // End time = start + slots * 30min. TIMES has start-of-slot labels 14:30..19:00.
  // e.g. startIdx=0 (14:30), slots=2 → end 15:30 (TIMES[2])
  const endIdx = startIdx + slots;
  if (endIdx < TIMES.length) return TIMES[endIdx];
  // beyond 19:00 slot start → the only valid one is 19:30
  return "19:30";
}

function SessionDetail({ session, groups, registrations, coaches, courts, trainers, allSessions, config, onToggleExpected }) {
  const isAthletic = session.type === "athletic";
  const group = groups.find((g) => g.id === session.group_id);
  const court = courts.find((c) => c.id === session.court_id);
  // Athletic sessions store trainer_id in coach_ids; tennis sessions store coach ids
  const sessCoaches = isAthletic
    ? (trainers || []).filter((t) => session.coach_ids?.includes(t.id))
    : coaches.filter((c) => session.coach_ids?.includes(c.id));
  const members = (group?.registration_ids || [])
    .map((id) => registrations.find((r) => r.id === id))
    .filter(Boolean);

  // "Perché qui?" — compute top 3 alternative day+time+court that would fit this session's duration,
  // scored on member preferences (green=+2, yellow/parziale=+1, red=-3, missing=-0.5), penalizing
  // slots that clash with existing sessions on the same court or same coach.
  const alternatives = useMemo(() => {
    if (!allSessions || isAthletic) return [];
    const need = timeToSlots(session.start_time, session.end_time);
    const otherSessions = allSessions.filter((s) => s !== session && s.type !== "athletic");
    const daysUsedForGroup = new Set(
      allSessions.filter((s) => s !== session && s.group_id === session.group_id && s.type === "tennis").map((s) => s.day)
    );
    const dayIndex = (dk) => DAYS.findIndex((x) => x.key === dk);
    const candidates = [];
    for (const d of DAYS) {
      const di = dayIndex(d.key);
      const close = [...daysUsedForGroup].some((ad) => Math.abs(dayIndex(ad) - di) <= 1);
      if (close) continue;
      const courtHours = (config?.day_settings?.[d.key]?.court_hours) || {};
      for (const c of courts) {
        for (let ti = 0; ti + need <= TIMES.length; ti++) {
          const times = TIMES.slice(ti, ti + need);
          const hoursForCourt = courtHours[c.id];
          if (hoursForCourt && !times.every((t) => hoursForCourt.includes(t))) continue;
          // Skip if any physical overlap on this court/time
          const clash = otherSessions.some((s) => {
            if (s.day !== d.key || s.court_id !== c.id) return false;
            const oStart = TIMES.indexOf(s.start_time);
            const oSlots = timeToSlots(s.start_time, s.end_time);
            return ti < oStart + oSlots && oStart < ti + need;
          });
          if (clash) continue;
          let score = 0;
          for (const m of members) {
            const disp = { ...(m.disponibilita || {}), ...(m.disponibilita_admin || {}) };
            let hasRed = false, allGreen = true, anyDeclared = false;
            for (const t of times) {
              const v = disp[`${d.key}_${t}`];
              if (v) anyDeclared = true;
              if (v === "red") hasRed = true;
              if (v !== "green") allGreen = false;
            }
            if (hasRed) score -= 3;
            else if (allGreen) score += 2;
            else if (anyDeclared) score += 1;
            else score -= 0.5;
          }
          candidates.push({
            day: d.key,
            dayLabel: d.label,
            time: times[0],
            end: times[times.length - 1],
            court: c.nome,
            score: Math.round(score * 10) / 10,
          });
        }
      }
    }
    candidates.sort((a, b) => b.score - a.score);
    return candidates.slice(0, 3);
  }, [session, allSessions, courts, members, config, isAthletic]);

  const dayLabel = DAYS.find((d) => d.key === session.day)?.label || session.day;
  const locationLabel = isAthletic
    ? `Preparazione atletica${session.athletic_label ? ` · Slot "${session.athletic_label}"` : ""}`
    : `${court?.nome || session.court_id}${court?.tipo ? ` (${court.tipo})` : ""}`;

  return (
    <>
      <DialogHeader>
        <div className="flex items-center gap-2">
          {isAthletic ? (
            <span className="w-6 h-6 rounded-full bg-orange-500 text-white flex items-center justify-center">
              <Dumbbell className="w-3.5 h-3.5" />
            </span>
          ) : (
            <span className="w-3 h-3 rounded-full" style={{ background: group?.color || "#64748b" }} />
          )}
          <DialogTitle className="text-xl">{group?.nome || "?"}</DialogTitle>
        </div>
        <DialogDescription>
          {dayLabel} · {session.start_time}–{session.end_time} · {locationLabel}
        </DialogDescription>
      </DialogHeader>

      <div className="mt-3 space-y-3">
        <section>
          <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">
            {isAthletic ? "Preparatore atletico" : "Maestri"}
          </h4>
          {sessCoaches.length === 0 ? (
            <p className="text-xs text-slate-400 italic">
              {isAthletic ? "Nessun preparatore configurato" : "Nessun maestro assegnato"}
            </p>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {sessCoaches.map((c) => (
                <span key={c.id} className={cn(
                  "text-xs px-2 py-1 rounded-full font-semibold",
                  isAthletic ? "bg-orange-100 text-orange-800" : "bg-slate-100 text-slate-800"
                )}>
                  {c.nome}
                </span>
              ))}
            </div>
          )}
        </section>


        {!isAthletic && alternatives && alternatives.length > 0 && (
          <section className="rounded-lg bg-indigo-50 border border-indigo-200 p-2.5" data-testid="why-here-section">
            <h4 className="text-xs font-bold uppercase tracking-wider text-indigo-700 mb-1.5">Perché qui? Migliori alternative</h4>
            <ul className="space-y-1 text-xs text-indigo-900">
              {alternatives.map((a, i) => (
                <li key={i} className="flex items-center justify-between gap-2">
                  <span>{a.dayLabel} · {a.time}–{a.end} · {a.court}</span>
                  <span className="font-mono text-[10px] bg-white/60 rounded px-1.5 py-0.5">score {a.score}</span>
                </li>
              ))}
            </ul>
            <p className="text-[10px] text-indigo-700/80 mt-1.5">
              Punteggio: verde +2, giallo/parziale +1, "impossibile" -3, non dichiarato -0.5. Slot già occupati e giorni ravvicinati sono esclusi.
            </p>
          </section>
        )}

        <section>
          <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">
            Allievi ({members.length - (session.excluded_ids || []).filter((id) => members.some((m) => m.id === id)).length} previsti su {members.length})
          </h4>
          {onToggleExpected && (
            <p className="text-[11px] text-slate-500 mb-1.5">Togli la spunta a chi non si allena in questo giorno: non conterà come assente. Ricordati di salvare le modifiche e una nuova versione.</p>
          )}
          {(() => {
            // Compute per-member availability status for this session's slots
            const slotsSpan = timeToSlots(session.start_time, session.end_time);
            const startIdx = TIMES.indexOf(session.start_time);
            const perMember = members.map((m) => {
              const disp = { ...(m.disponibilita || {}), ...(m.disponibilita_admin || {}) };
              const values = [];
              for (let i = 0; i < slotsSpan; i++) {
                const t = TIMES[startIdx + i];
                if (t) values.push(disp[`${session.day}_${t}`] || null);
              }
              let status = "missing";
              if (values.some((v) => v === "red")) status = "red";
              else if (values.every((v) => v === "green")) status = "green";
              else if (values.every((v) => v)) status = "yellow"; // mixed green/yellow but all declared
              else if (values.some((v) => v)) status = "partial"; // some slots declared, others missing
              return { m, status };
            });
            const missing = perMember.filter((x) => x.status === "missing").map((x) => x.m);
            const partial = perMember.filter((x) => x.status === "partial").map((x) => x.m);
            const reds = perMember.filter((x) => x.status === "red").map((x) => x.m);
            const statusMeta = {
              green: { dot: "bg-emerald-500", label: "Verde", cls: "text-emerald-700" },
              yellow: { dot: "bg-yellow-400", label: "Giallo", cls: "text-yellow-700" },
              red: { dot: "bg-red-500", label: "Impossibile", cls: "text-red-700" },
              partial: { dot: "bg-slate-400 ring-1 ring-yellow-400", label: "Parziale", cls: "text-slate-700" },
              missing: { dot: "bg-slate-300 ring-1 ring-dashed ring-slate-400", label: "Non dichiarato", cls: "text-slate-500" },
            };
            return (
              <>
                <ul className="space-y-1">
                  {perMember.map(({ m, status }) => {
                    const meta = statusMeta[status];
                    const expected = !(session.excluded_ids || []).includes(m.id);
                    return (
                      <li key={m.id} className={cn("text-sm flex items-center justify-between gap-2", expected ? "text-slate-800" : "text-slate-400")}>
                        <span className="flex items-center gap-1.5 min-w-0">
                          {onToggleExpected ? (
                            <Checkbox checked={expected} onCheckedChange={() => onToggleExpected(session, m.id)}
                              title="Previsto in questo giorno" data-testid={`expected-toggle-${m.id}`} />
                          ) : !expected && <span className="text-[10px] font-semibold uppercase">non previsto</span>}
                          <span
                            className={cn("inline-block w-2.5 h-2.5 rounded-full shrink-0", meta.dot)}
                            title={`Disponibilità: ${meta.label}`}
                          />
                          <span className={cn("truncate", !expected && "line-through")}>{m.figlio.nome} {m.figlio.cognome}</span>
                        </span>
                        <span className="text-xs text-slate-500 font-mono shrink-0">{m.genitore.telefono}</span>
                      </li>
                    );
                  })}
                </ul>
                {(missing.length > 0 || partial.length > 0 || reds.length > 0) && (
                  <div className="mt-2 pt-2 border-t border-slate-200 space-y-1 text-[11px]">
                    {reds.length > 0 && (
                      <div className="text-red-700">
                        <b>❌ Impossibile</b> per {reds.length}: {reds.map((r) => `${r.figlio.nome} ${r.figlio.cognome}`).join(", ")}
                      </div>
                    )}
                    {missing.length > 0 && (
                      <div className="text-slate-600">
                        <b>❓ Nessuna preferenza dichiarata</b> per {missing.length}: {missing.map((r) => `${r.figlio.nome} ${r.figlio.cognome}`).join(", ")}
                      </div>
                    )}
                    {partial.length > 0 && (
                      <div className="text-slate-600">
                        <b>◐ Preferenza parziale</b> ({partial.length}): {partial.map((r) => `${r.figlio.nome} ${r.figlio.cognome}`).join(", ")}
                      </div>
                    )}
                  </div>
                )}
              </>
            );
          })()}
        </section>

        {session.conflicts?.length > 0 && (
          <section>
            <h4 className="text-xs font-bold uppercase tracking-wider text-amber-700 mb-1.5 flex items-center gap-1">
              <AlertTriangle className="w-3.5 h-3.5" /> Criticità
            </h4>
            <ul className="space-y-1 text-xs text-amber-800">
              {session.conflicts.map((c, i) => (
                <li key={i}>• {c}</li>
              ))}
            </ul>
          </section>
        )}
      </div>
    </>
  );
}
